import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ICON_STROKE } from '../../constants/icon-stroke';
import { apiClient } from '../../services/api';
import { reservationsService, type Reservation as ApiReservation } from '../../services/reservations';
import { membershipService } from '../../services/membership.service';
import { golfClubsService } from '../../services/golf-clubs';
import { walletApi } from '../../services/wallet';
import { passesService } from '../../services/passes';
import { giftCardsService, type GiftCardSummary } from '../../services/gift-cards';
import BookingGolferGiftCardsPanel from './BookingGolferGiftCardsPanel';
import { WalletAddButtons } from '../OnlineBooking/WalletAddButtons';
import { customerAuthService } from '../../services/customer-auth.service';
import type { CommunicationPreferences } from '../../services/customer-auth.service';
import { format } from 'date-fns';
import { Switch, FormControlLabel } from '@mui/material';
import { bookingBrandInfoChromeStyles, teboxDesignSystem, useBookingTebox } from '../../styles/tebox-booking';
import {
  BOOKING_RESERVED_ACTIVE_BG,
} from '../design-system/pills/pillStyles';
import {
  bookingBrandInfoPillDataAttrs,
  getBookingBrandInfoPillStyles,
} from '../../utils/booking-brand-info-pill';
import { BookingsIcon } from '../Icons/BookingsIcon';
import WalletIcon from '../Icons/WalletIcon';
import GiftCardIcon from '../Icons/GiftCardIcon';
import { PassesIcon } from '../Icons/PassesIcon';
import CustomersIcon from '../Icons/CustomersIcon';
import ContactIcon from '../Icons/ContactIcon';
import {
  canonicalStatusDisplayLabel,
  titleCaseDashboardWords,
} from '@boubou/tebox-copy';

/**
 * Dashboard tab switcher uses this breakpoint (not the global 768px booking “phone” flag):
 * tablets and narrow laptops get the section dropdown instead of sidebar + tabs.
 * Wide layout (>1024px): full-width sticky header, then inline left sidebar row + content.
 */
const DASHBOARD_COMPACT_MAX_PX = 1023;
/**
 * Phone-width dashboard: stack greeting + CTAs, tighter horizontal insets / safe-area.
 * Uses `max-width: 767px` so tablet portrait (≥768) keeps header row + compact dropdown vs inline sidebar beyond 1023px.
 */
const DASHBOARD_PHONE_LAYOUT_MAX_PX = 767;

/** Wide layout: fixed inline sidebar width (`flex-shrink: 0`), not overlaid/fixed positioning. */
const DASHBOARD_INLINE_SIDEBAR_WIDTH_PX = 220;

/** Bordered stacks / tables — aligns with golfer `designSystem.borderRadius.borderedShell`. */
const GOLFER_DASHBOARD_SHELL_RADIUS = 'var(--brand-border-radius, var(--tebox-radius-container, 6px))';

/** Guest dashboard lists — spaced bordered cards instead of admin grid tables / single bordered table shells. */
const golferDashboardGuestListOuterStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: teboxDesignSystem.spacing.sm,
};
const golferDashboardGuestListRowCardStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: GOLFER_DASHBOARD_SHELL_RADIUS,
  border: `1px solid ${teboxDesignSystem.colors.border.light}`,
  backgroundColor: teboxDesignSystem.colors.background.primary,
  padding: teboxDesignSystem.spacing.md,
};

/** Empty states — same shell vocabulary as guest row-cards. */
const golferDashboardEmptyStateShellStyle: React.CSSProperties = {
  ...golferDashboardGuestListRowCardStyle,
  padding: teboxDesignSystem.spacing.lg,
  textAlign: 'center',
  boxSizing: 'border-box',
};

function GolferDashboardEmptyStateShell({ children }: { children: React.ReactNode }) {
  return (
    <div role="status" style={golferDashboardEmptyStateShellStyle}>
      {children}
    </div>
  );
}

/** Inline banner for expired payment links (avoid `alert`). */
function GolferDashboardPaymentLinkExpiredBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  const sb = teboxDesignSystem.spacing;
  return (
    <div
      role="alert"
      style={{
        ...golferDashboardGuestListRowCardStyle,
        marginBottom: sb.md,
        textAlign: 'left',
        borderColor: teboxDesignSystem.colors.status.error,
        backgroundColor: teboxDesignSystem.colors.status.errorLight,
        display: 'flex',
        flexDirection: 'column',
        gap: sb.sm,
      }}
    >
      <div
        style={{
          ...teboxDesignSystem.typography.body,
          color: teboxDesignSystem.colors.text.primary,
        }}
      >
        {message}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        style={{
          alignSelf: 'flex-start',
          ...teboxDesignSystem.components.button.secondary,
          padding: `${sb.xs} ${sb.sm}`,
          fontSize: teboxDesignSystem.typography.body.fontSize,
          fontWeight: 500,
          outlineColor: teboxDesignSystem.colors.accent.primaryDarker,
        }}
      >
        Dismiss
      </button>
    </div>
  );
}

function BookingGolferPassesLoadingSkeleton() {
  const bar = teboxDesignSystem.colors.background.secondary;
  return (
    <div style={golferDashboardGuestListOuterStyle}>
      {[0, 1, 2].map((k) => (
        <div
          key={k}
          style={{
            ...golferDashboardGuestListRowCardStyle,
            display: 'flex',
            gap: teboxDesignSystem.spacing.md,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: teboxDesignSystem.spacing.sm,
              minWidth: 0,
            }}
          >
            <div style={{ height: 14, borderRadius: 6, backgroundColor: bar, width: '55%', maxWidth: 220 }} />
            <div
              style={{ height: 12, borderRadius: 6, backgroundColor: bar, opacity: 0.88, width: '88%', maxWidth: 360 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function golferDashboardMuiSwitchSx(primaryColor: string) {
  return {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: primaryColor,
      '&:hover': {
        backgroundColor: `color-mix(in srgb, ${primaryColor} 14%, transparent)`,
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: primaryColor,
    },
    '& .MuiSwitch-switchBase.Mui-focusVisible': {
      outline: `2px solid ${teboxDesignSystem.colors.accent.primaryDarker}`,
      outlineOffset: 2,
    },
  } as const;
}

const pc = teboxDesignSystem.components.pill.colors;
const golferStatusPill = pc.golferStatus;

/**
 * Admin tee sheet (`PlayerCell`) treats “paid” when reservation payment fields line up
 * **or** a player row is paid with a payment method — often while lifecycle `status` stays `reserved`.
 * Mirror that here so golfer-facing labels match staff.
 */
function golferReservationStatusForDisplay(reservation: {
  status: string;
  paymentStatus?: string | null;
  paidAt?: string | null;
  paymentMethod?: string | null;
  paymentTransactionId?: string | null;
  players?: Array<{ isPaid?: boolean | null; paymentMethod?: string | null }>;
}): string {
  const lifecycle = String(reservation.status ?? '').trim();
  const lc = lifecycle.toLowerCase();
  if (lc === 'paid') return lifecycle;

  const payRaw = String(reservation.paymentStatus ?? '').trim();
  const pay = payRaw.toLowerCase();
  if (pay === 'partial') return 'partial';

  const hasPaymentInfo =
    !!reservation.paymentMethod &&
    (!!reservation.paidAt || !!reservation.paymentTransactionId);
  const paymentStatusPaid =
    (pay === 'paid' || payRaw === 'Paid' || payRaw === 'PAID') && hasPaymentInfo;

  const players = reservation.players;
  const playerPaidLikeStaff =
    Array.isArray(players) &&
    players.some((p) => p?.isPaid === true && !!p?.paymentMethod);
  const anyPlayerMarkedPaid =
    Array.isArray(players) && players.some((p) => p?.isPaid === true);

  if (paymentStatusPaid || playerPaidLikeStaff) return 'paid';
  // $0 / comp paths may set isPaid without paymentMethod on the player row.
  if (anyPlayerMarkedPaid) return 'paid';

  // Recorded payment time but missing one of the fields staff UI requires — still show Paid.
  if (reservation.paidAt) return 'paid';

  return lifecycle;
}

/** Reservation chips: paid/cancelled/pending semantic colours; reserved/active use club primary. */
function golferReservationStatusAccent(status: string, clubPrimaryHex: string): string {
  const primary = String(clubPrimaryHex || '').trim();
  const reservedFill =
    primary.length > 0 ? primary : BOOKING_RESERVED_ACTIVE_BG;
  const s = (status || '').toLowerCase().replace(/_/g, '-');
  if (s === 'paid') return pc.success;
  if (s === 'partial') return pc.warning;
  if (['confirmed', 'completed', 'checked-in', 'checked_in'].includes(s)) return pc.success;
  if (s === 'reserved' || s === 'active') return reservedFill;
  if (['cancelled', 'canceled', 'no-show', 'no_show', 'closed'].includes(s))
    return golferStatusPill.cancelledReservation;
  if (['pending', 'draft'].includes(s)) return golferStatusPill.pendingReservation;
  return pc.neutral;
}

function golferReservationStatusLabel(status: string): string {
  const raw = status ?? '';
  return canonicalStatusDisplayLabel(raw) ?? titleCaseDashboardWords(raw);
}

function golferInstallmentStatusAccent(status: string): string {
  const s = (status || '').toUpperCase();
  if (s === 'PAID') return pc.success;
  /** Match reservations “pending” orange (distinct from amber partial / warning chips). */
  if (s === 'PENDING') return golferStatusPill.pendingReservation;
  return pc.neutral;
}

function golferInstallmentStatusLabel(status: string): string {
  const raw = status ?? '';
  return canonicalStatusDisplayLabel(raw) ?? titleCaseDashboardWords(raw);
}

function measureGolferDashboardViewport(width: number): { compact: boolean; phone: boolean } {
  return {
    compact: width <= DASHBOARD_COMPACT_MAX_PX,
    phone: width <= DASHBOARD_PHONE_LAYOUT_MAX_PX,
  };
}

/** Tab dropdown when `compact`; stacked header / gutters when `phone`. */
function useGolferDashboardViewport(): { compact: boolean; phone: boolean } {
  const [viewport, setViewport] = useState(() =>
    typeof window !== 'undefined'
      ? measureGolferDashboardViewport(window.innerWidth)
      : { compact: false, phone: false },
  );
  useEffect(() => {
    const update = () => setViewport(measureGolferDashboardViewport(window.innerWidth));
    update();
    window.addEventListener('resize', update);
    const mqCompact = window.matchMedia(`(max-width: ${DASHBOARD_COMPACT_MAX_PX}px)`);
    const mqPhone = window.matchMedia(`(max-width: ${DASHBOARD_PHONE_LAYOUT_MAX_PX}px)`);
    mqCompact.addEventListener('change', update);
    mqPhone.addEventListener('change', update);
    return () => {
      window.removeEventListener('resize', update);
      mqCompact.removeEventListener('change', update);
      mqPhone.removeEventListener('change', update);
    };
  }, []);
  return viewport;
}

/** CUID / opaque ids should not be shown as user-facing copy when labels exist or as fallback. */
function looksLikeOpaqueRecordId(value: string | undefined | null): boolean {
  if (!value || typeof value !== 'string') return false;
  const v = value.trim();
  if (v.length < 20) return false;
  return /^c[a-z0-9]{20,}$/i.test(v);
}

function passUsageProductDisplay(u: {
  productUsed?: string;
  productUsedLabel?: string;
  productUsedName?: string;
}): string {
  const label = u.productUsedLabel || u.productUsedName;
  if (label) return label;
  if (looksLikeOpaqueRecordId(u.productUsed)) return 'Included product';
  return (u.productUsed && String(u.productUsed).trim()) || '—';
}

function passUsageReservationDisplay(u: { reservationId?: string | null; reservationLabel?: string }): string {
  if (u.reservationLabel) return u.reservationLabel;
  if (u.reservationId) return 'Golf reservation';
  return '—';
}

interface BrandColors {
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  fontFamily: string;
  buttonColor: string;
  buttonTextColor: string;
  primaryTextColor: string;
  /** Club primary accent from brand settings; falls back to `buttonColor` when omitted. */
  accentColor?: string;
}

interface GolferDashboardProps {
  onBackToBooking: () => void;
  brandColors?: BrandColors;
  /** When the parent booking shell shows “Hello, …” in its mobile header (next to account), hide the duplicate `<h1>` here. */
  omitWelcomeHeading?: boolean;
}

/** API reservation plus optional UI fields the dashboard may show when present. */
type Reservation = ApiReservation & { totalAmount?: number };

interface CustomerProfile {
  id: string;
  /** Set from API after login /me — used to load club policies via public booking APIs. */
  clubId?: string;
  clubNumber?: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  customerType: string;
  customerTypeId: string;
  customerTypeName?: string | null;
  dateOfBirth?: string | null;
  preferences?: string;
  showNameOnTeeSheet?: boolean;
  communicationPreferences?: CommunicationPreferences;
}

const DEFAULT_COMMUNICATION_PREFS: CommunicationPreferences = {
  emailNotifications: true,
  smsNotifications: true,
  marketingEmails: false,
  reservationReminders: true,
  paymentReminders: true,
  promotionalOffers: false,
};

interface MembershipSubscription {
  id: string;
  customerId: string;
  productId: string;
  startDate: string;
  endDate?: string;
  status: string;
  product: {
    id: string;
    name: string;
    price: number;
    billingType: string;
    installments?: number;
    monthlyDueAmount?: number;
  };
  paymentSchedules: PaymentSchedule[];
}

// Helper function to check if a customer is a member
const isMember = (customer: CustomerProfile | null): boolean => {
  if (!customer?.customerType) return false;

  const raw = customer.customerType as unknown;
  if (typeof raw === 'object' && raw !== null && 'category' in raw) {
    const cat = String((raw as { category?: string }).category ?? '').toLowerCase();
    if (cat === 'member') return true;
  }

  const customerType = String(raw).toLowerCase().trim();
  if (customerType === 'member') return true;
  const memberNames = ['unlimited', 'ladies intermediate', 'full member', 'weekday member'];
  return memberNames.includes(customerType);
};

function profileFormFromCustomer(c: CustomerProfile) {
  return {
    firstName: c.firstName || '',
    lastName: c.lastName || '',
    email: c.email || '',
    phone: c.phone || '',
    dateOfBirth: c.dateOfBirth ? String(c.dateOfBirth).slice(0, 10) : '',
    preferences: c.preferences ?? '',
  };
}

interface PaymentSchedule {
  id: string;
  subscriptionId: string;
  installmentNumber: number;
  dueDate: string;
  amount: number;
  status: string;
  paidAt?: string;
  paymentMethod?: string;
  paymentLinks: PaymentLink[];
}

interface PaymentLink {
  id: string;
  subscriptionId: string;
  paymentScheduleId: string;
  amount: number;
  expiresAt: string;
  customerEmail: string;
  customerName: string;
  url: string;
  token: string;
  usedAt?: string;
}

function formatBookingClockTime(date: Date): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  /** No leading zero on hour (e.g. `1:00 PM`, not `01:00 PM`). */
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

/**
 * When `teeTimeId` is a CUID (generated tee time), `new Date(teeTimeId)` is invalid.
 * Prefer slot start from the API-enriched `teeTimeSlot`, then ISO `teeTimeId`, then calendar `teeTimeDate`.
 */
function getBookingReservationPlayDate(reservation: Reservation): Date | null {
  const startTs = reservation.teeTimeSlot?.startTs;
  if (startTs != null && startTs !== '') {
    const d = new Date(startTs as string | Date);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const tid = reservation.teeTimeId;
  if (tid && String(tid).includes('T')) {
    const raw = String(tid);
    const pipe = raw.indexOf('|');
    const main = pipe >= 0 ? raw.slice(0, pipe) : raw;
    const tIndex = main.indexOf('T');
    if (tIndex > 0) {
      const dateStr = main.slice(0, tIndex);
      const timeMatch = main.slice(tIndex + 1).match(/^(\d{1,2}):(\d{2})/);
      if (timeMatch && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-').map((x) => parseInt(x, 10));
        const hh = parseInt(timeMatch[1], 10);
        const mm = parseInt(timeMatch[2], 10);
        const local = new Date(y, (m || 1) - 1, d || 1, hh, mm);
        if (!Number.isNaN(local.getTime())) return local;
      }
    }
    const d = new Date(main);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const td = reservation.teeTimeDate;
  if (typeof td === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(td)) {
    const d = new Date(`${td}T12:00:00`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function formatBookingReservationTeeTime(reservation: Reservation): { date: string; time: string } {
  const d = getBookingReservationPlayDate(reservation);
  if (!d || Number.isNaN(d.getTime())) {
    return { date: '—', time: '—' };
  }
  return {
    date: format(d, 'MM/dd/yy'),
    time: format(d, 'h:mm a'),
  };
}

function formatBookingReservationAmount(amount: number): string {
  const n = typeof amount === 'number' && !Number.isNaN(amount) ? amount : Number(amount);
  if (Number.isNaN(n)) return '$0.00';
  return `$${n.toFixed(2)}`;
}

function parseLaneKeyFromBookingTeeTimeId(teeTimeId: string): string | null {
  const s = String(teeTimeId || '');
  const pipe = s.indexOf('|');
  if (pipe === -1) return null;
  const lane = s.slice(pipe + 1).trim();
  if (!lane || lane === 'default') return null;
  return lane.replace(/-/g, ' ');
}

type ReceiptLineItem = {
  label: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  spotLabel?: string;
};

function collectBookingReceiptLineItems(res: unknown): ReceiptLineItem[] {
  if (!res || typeof res !== 'object') return [];
  const players = (res as { players?: unknown }).players;
  if (!Array.isArray(players)) return [];
  const lines: ReceiptLineItem[] = [];
  for (const p of players) {
    if (!p || typeof p !== 'object') continue;
    const spot = (p as { spotNumber?: number }).spotNumber;
    const spotLabel = typeof spot === 'number' ? `Player ${spot}` : undefined;
    const prods = (p as { products?: unknown }).products;
    if (!Array.isArray(prods)) continue;
    for (const rp of prods) {
      if (!rp || typeof rp !== 'object') continue;
      const row = rp as Record<string, unknown>;
      const product = row.product as Record<string, unknown> | undefined;
      const name =
        (typeof row.productName === 'string' && row.productName) ||
        (product && typeof product.name === 'string' ? product.name : null) ||
        'Item';
      const qty = typeof row.quantity === 'number' ? row.quantity : Number(row.quantity) || 1;
      const unitPrice = Number(row.unitPrice ?? 0);
      const lineTotal = Number(row.totalPrice ?? unitPrice * qty);
      lines.push({ label: name, qty, unitPrice, lineTotal, spotLabel });
    }
  }
  return lines;
}

/** Cart rental lines on a reservation — total matches online tee card “incl. Cart” when present. */
function bookingReservationLineItemsIncludeCart(res: unknown): boolean {
  const cat = (row: Record<string, unknown>): string | null => {
    const product = row.product as Record<string, unknown> | undefined;
    const c =
      (typeof row.productCategory === 'string' && row.productCategory) ||
      (product && typeof product.category === 'string' ? product.category : null);
    return c ? c.toLowerCase().trim() : null;
  };
  if (!res || typeof res !== 'object') return false;
  const players = (res as { players?: unknown }).players;
  if (!Array.isArray(players)) return false;
  for (const p of players) {
    if (!p || typeof p !== 'object') continue;
    const prods = (p as { products?: unknown }).products;
    if (!Array.isArray(prods)) continue;
    for (const rp of prods) {
      if (!rp || typeof rp !== 'object') continue;
      const row = rp as Record<string, unknown>;
      const product = row.product as Record<string, unknown> | undefined;
      const name =
        (typeof row.productName === 'string' && row.productName) ||
        (product && typeof product.name === 'string' ? product.name : '') ||
        '';
      const category = cat(row);
      if (category === 'cart') return true;
      if (/\bcart\b/i.test(name)) return true;
    }
  }
  return false;
}

function bookingReceiptTotalDollars(res: unknown): number {
  if (!res || typeof res !== 'object') return 0;
  const o = res as Record<string, unknown>;
  const ta = o.totalAmount;
  if (typeof ta === 'number' && !Number.isNaN(ta)) return ta;
  const pc = o.priceCents;
  if (typeof pc === 'number' && !Number.isNaN(pc)) return pc / 100;
  return collectBookingReceiptLineItems(res).reduce((s, l) => s + l.lineTotal, 0);
}

function formatBookingReceiptHoleSet(holeSet: unknown): string | null {
  if (holeSet == null || typeof holeSet !== 'string') return null;
  const s = holeSet.toLowerCase().replace(/_/g, '-');
  if (s === '18' || s === '18-hole') return '18 holes';
  if (s === '9' || s === '9-hole') return '9 holes';
  return holeSet.replace(/-/g, ' ');
}

type BookingDashboardDs = {
  colors: {
    primary: string;
    white: string;
    gray: Record<string, string>;
  };
  spacing: Record<string, string>;
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    /** Club brand curve on bordered summary cards (matches booking shell tokens). */
    borderedShell: string;
  };
  typography: {
    body: React.CSSProperties;
    caption: React.CSSProperties;
  };
};

/** Mirrors `TableTotalDisplay` `size="sm"` (apps/web) for booking inline styles. */
function bookingDashboardCurrencyTotalSm(amount: number, ds: BookingDashboardDs): React.ReactNode {
  const absValue = Math.abs(amount);
  const [integerPart, decimalPart] = absValue.toFixed(2).split('.');
  const formattedInteger = Number(integerPart).toLocaleString('en-US');
  const prefix = amount < 0 ? '-$' : '$';
  const amtColor = ds.colors.gray[900];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
      <span
        style={{
          fontSize: teboxDesignSystem.typography.h4.fontSize,
          fontWeight: 500,
          lineHeight: '24px',
          color: amtColor,
        }}
      >
        {prefix}
        {formattedInteger}
      </span>
      <span
        style={{
          fontSize: teboxDesignSystem.typography.body.fontSize,
          fontWeight: 400,
          lineHeight: '24px',
          color: amtColor,
          marginLeft: '2px',
        }}
      >
        .{decimalPart}
      </span>
    </span>
  );
}

function bookingPassCoerceNumber(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function formatPassStatusLabel(status: string): string {
  const raw = status ?? '';
  return canonicalStatusDisplayLabel(raw) ?? titleCaseDashboardWords(raw);
}

function golferPassStatusAccentHex(status: string, activeFillHex: string): string {
  const primary = String(activeFillHex || '').trim();
  const activeFill =
    primary.length > 0 ? primary : BOOKING_RESERVED_ACTIVE_BG;
  const s = (status || '').toLowerCase().trim();
  if (s === 'active') return activeFill;
  if (['paid', 'issued'].includes(s)) return pc.success;
  if (
    ['expired', 'cancelled', 'canceled', 'depleted', 'suspended', 'inactive', 'archived'].includes(s)
  ) {
    return golferStatusPill.cancelledReservation;
  }
  if (['pending'].includes(s)) return golferStatusPill.pendingReservation;
  return pc.neutral;
}

/** Pass cards are de-emphasized when not usable: terminal status, no uses left, or past validTo. */
function bookingPassShouldGreyOut(pass: any, usesRem: number, unitsRem: number | null, hasUnits: boolean): boolean {
  const statusNorm = String(pass.status ?? '').trim().toLowerCase();
  const inactiveStatuses = new Set([
    'depleted',
    'expired',
    'cancelled',
    'canceled',
    'archived',
    'inactive',
    'suspended',
  ]);
  if (inactiveStatuses.has(statusNorm)) return true;

  const fullyUsed = usesRem <= 0 || (hasUnits && unitsRem != null && unitsRem <= 0);
  if (fullyUsed) return true;

  if (pass.validTo) {
    try {
      const end = new Date(pass.validTo);
      if (!Number.isNaN(end.getTime()) && end.getTime() < Date.now()) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}

type PassUsageRow = {
  id: string;
  usageDate?: string;
  productUsed?: string;
  productUsedLabel?: string;
  productUsedName?: string;
  reservationId?: string | null;
  reservationLabel?: string;
};

function BookingGolferPassUsagesStacked({ usages }: { usages: PassUsageRow[] }) {
  const body = teboxDesignSystem.typography.body;
  const labelStyle = {
    ...teboxDesignSystem.typography.caption,
    color: teboxDesignSystem.colors.text.secondary,
    marginBottom: teboxDesignSystem.spacing.xs,
    display: 'block' as const,
  };
  return (
    <div style={golferDashboardGuestListOuterStyle}>
      {usages.map((u) => {
        let dateLine = '—';
        try {
          const d = u.usageDate != null && u.usageDate !== '' ? new Date(u.usageDate) : null;
          dateLine = d && !Number.isNaN(d.getTime()) ? format(d, 'MMM dd, yyyy') : '—';
        } catch {
          /* keep fallback */
        }
        return (
          <div
            key={u.id}
            style={{
              ...golferDashboardGuestListRowCardStyle,
              display: 'flex',
              flexDirection: 'column',
              gap: teboxDesignSystem.spacing.sm,
            }}
          >
            <div
              style={{
                ...body,
                fontWeight: 500 as const,
                color: teboxDesignSystem.colors.text.primary,
              }}
            >
              {dateLine}
            </div>
            <div>
              <span style={labelStyle}>Product / service</span>
              <div style={{ ...body, wordBreak: 'break-word', color: teboxDesignSystem.colors.text.primary }}>
                {passUsageProductDisplay(u)}
              </div>
            </div>
            <div>
              <span style={labelStyle}>Reservation</span>
              <div style={{ ...body, wordBreak: 'break-word', color: teboxDesignSystem.colors.text.primary }}>
                {passUsageReservationDisplay(u)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BookingGolferPassesPanel({
  ds,
  passes,
  loading,
}: {
  ds: BookingDashboardDs;
  passes: any[];
  loading: boolean;
}) {
  if (loading) {
    return <BookingGolferPassesLoadingSkeleton />;
  }
  if (!passes.length) {
    return (
      <GolferDashboardEmptyStateShell>
        <p style={{ ...ds.typography.body, margin: 0, maxWidth: 420, marginInline: 'auto' }}>
          You don&apos;t have any passes yet. Passes you purchase will appear here with usage history.
        </p>
      </GolferDashboardEmptyStateShell>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: teboxDesignSystem.spacing.stackGapTight,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {passes.map((p) => (
        <BookingGolferPassCard key={p.id} ds={ds} pass={p} />
      ))}
    </div>
  );
}

function BookingGolferPassCard({
  ds,
  pass,
}: {
  ds: BookingDashboardDs;
  pass: any;
}) {
  const body = ds.typography.body;
  const font = body.fontFamily as string;
  const name = pass.passProduct?.name ?? 'Pass';
  const usesRem = bookingPassCoerceNumber(pass.totalUsesRemaining);
  const usesAvail = bookingPassCoerceNumber(pass.totalUsesAvailable);
  const hasUnits = pass.unitsRemaining != null && pass.unitsRemaining !== '';
  const unitsRem = hasUnits ? bookingPassCoerceNumber(pass.unitsRemaining) : null;
  const usages: PassUsageRow[] = Array.isArray(pass.usages) ? pass.usages : [];

  let purchaseShort = '—';
  let validToShort = '—';
  try {
    if (pass.purchaseDate) purchaseShort = format(new Date(pass.purchaseDate), 'MM/dd/yy');
  } catch {
    /* keep fallback */
  }
  try {
    if (pass.validTo) validToShort = format(new Date(pass.validTo), 'MM/dd/yy');
  } catch {
    /* keep fallback */
  }

  const greyOut = bookingPassShouldGreyOut(pass, usesRem, unitsRem, hasUnits);
  const cardText = (ds.typography.body.color as string) || teboxDesignSystem.colors.text.primary;
  const sb = teboxDesignSystem.spacing;
  const h3Title = {
    fontSize: teboxDesignSystem.typography.h4.fontSize,
    fontWeight: 500 as const,
    lineHeight: '21px',
    letterSpacing: 'normal' as const,
  };
  const statusRaw = String(pass.status ?? '');
  const balanceLine = `Balance ${usesRem} / ${usesAvail}`;
  const passStatusHex = golferPassStatusAccentHex(statusRaw, ds.colors.primary);

  return (
    <div
      style={{
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: ds.borderRadius.borderedShell,
        border: `1px solid ${teboxDesignSystem.colors.border.light}`,
        backgroundColor: teboxDesignSystem.colors.background.primary,
        boxShadow: teboxDesignSystem.shadows.sm,
        overflow: 'hidden',
        opacity: greyOut ? 0.88 : 1,
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: sb.lg,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: sb.md,
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <h3
              style={{
                margin: 0,
                marginBottom: sb.sm,
                fontFamily: font,
                fontWeight: h3Title.fontWeight,
                fontSize: h3Title.fontSize,
                lineHeight: h3Title.lineHeight,
                letterSpacing: h3Title.letterSpacing,
                color: cardText,
                wordBreak: 'break-word',
              }}
            >
              {name}
            </h3>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: sb.md,
                rowGap: sb.xs,
              }}
            >
              <span
                style={{
                  fontFamily: font,
                  fontSize: body.fontSize,
                  lineHeight: body.lineHeight,
                  color: cardText,
                }}
              >
                {purchaseShort}
              </span>
              <span
                {...bookingBrandInfoPillDataAttrs}
                style={getBookingBrandInfoPillStyles(passStatusHex)}
              >
                {formatPassStatusLabel(statusRaw)}
              </span>
              <span
                style={{
                  fontFamily: font,
                  fontSize: body.fontSize,
                  lineHeight: body.lineHeight,
                  color: cardText,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {balanceLine}
              </span>
            </div>
          </div>
          <div
            style={{
              flex: '0 0 auto',
              textAlign: 'right',
              fontFamily: font,
              fontSize: body.fontSize,
              lineHeight: body.lineHeight,
              color: cardText,
            }}
          >
            <div>{pass.validTo ? validToShort : 'No end date'}</div>
          </div>
        </div>
      </div>

      {usages.length > 0 ? (
        <div
          style={{
            padding: sb.lg,
          }}
        >
          <div
            style={{
              fontFamily: font,
              fontWeight: 500,
              fontSize: body.fontSize,
              lineHeight: body.lineHeight,
              margin: `0 0 ${sb.md} 0`,
              color: cardText,
            }}
          >
            Usage history
          </div>
          <BookingGolferPassUsagesStacked usages={usages} />
        </div>
      ) : null}
    </div>
  );
}

function bookingPlayerRowLabel(player: NonNullable<Reservation['players']>[number]): string {
  const guest = typeof player.guestName === 'string' ? player.guestName.trim() : '';
  if (guest) return guest;
  const c = player.customer;
  if (c) return `${c.firstName} ${c.lastName}`.trim();
  return '—';
}

function bookingPlayerTypeLabel(player: NonNullable<Reservation['players']>[number]): string {
  if (player.isGuest) return 'Guest';
  return 'Player';
}

function bookingGolferReservationRowDisplay(reservation: Reservation) {
  let clock = '—';
  let dateLine = '—';
  const play = getBookingReservationPlayDate(reservation);
  if (play && !Number.isNaN(play.getTime())) {
    clock = formatBookingClockTime(play);
    dateLine = format(play, 'MM/dd/yy');
  }
  const playerNames = (reservation.players ?? [])
    .map((p) => {
      const guest = typeof p.guestName === 'string' ? p.guestName.trim() : '';
      if (guest) return guest;
      const c = p.customer;
      if (c) return `${c.firstName} ${c.lastName}`.trim();
      return '';
    })
    .filter((n) => n.length > 0);
  const playersLine =
    reservation.playerCount +
    ' player' +
    (reservation.playerCount === 1 ? '' : 's') +
    (playerNames.length > 0 ? ` · ${playerNames.join(', ')}` : '');
  return { clock, dateLine, playersLine };
}

/** Clock + AM/PM split — matches `TeeTimeCard` `formatTimeParts` (zero-padded hour). */
function bookingGolferReservationClockPartsFromDate(d: Date | null): { clock: string; period: string } {
  if (!d || Number.isNaN(d.getTime())) return { clock: '—', period: '' };
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  const clock = `${displayHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  return { clock, period };
}

function bookingGolferReservationClockPartsForReservation(reservation: Reservation): {
  clock: string;
  period: string;
} {
  return bookingGolferReservationClockPartsFromDate(getBookingReservationPlayDate(reservation));
}

function bookingGolferReservationPlayerNamesLine(reservation: Reservation): string | null {
  const playerNames = (reservation.players ?? [])
    .map((p) => {
      const guest = typeof p.guestName === 'string' ? p.guestName.trim() : '';
      if (guest) return guest;
      const c = p.customer;
      if (c) return `${c.firstName} ${c.lastName}`.trim();
      return '';
    })
    .filter((n) => n.length > 0);
  if (playerNames.length === 0) return null;
  return playerNames.join(', ');
}

type GolferReservationCardBrand = {
  fontFamily: string;
  textColor: string;
  secondaryColor: string;
  accentColor: string;
  /** Reservation status pill background — club primary. */
  primaryColor: string;
};

/**
 * Reservation card — mirrors guest `TeeTimeCard` surface: hero time | total, divider, course, meta, footer row.
 */
function BookingGolferReservationCard({
  reservation,
  variant,
  onViewDetails,
  brand,
}: {
  reservation: Reservation;
  variant: 'upcoming' | 'past';
  onViewDetails: (r: Reservation) => void;
  brand: GolferReservationCardBrand;
}) {
  const tebox = useBookingTebox();
  const [isHovered, setIsHovered] = useState(false);
  const muted = variant === 'past';
  const body = teboxDesignSystem.typography.body;
  const bodySmall = teboxDesignSystem.typography.bodySmall;
  const { dateLine } = bookingGolferReservationRowDisplay(reservation);
  const { clock, period } = bookingGolferReservationClockPartsForReservation(reservation);
  const playerNamesLine = bookingGolferReservationPlayerNamesLine(reservation);
  const spotsBadge =
    reservation.playerCount === 1 ? '1 spot' : `${reservation.playerCount} spots`;
  const cardText = teboxDesignSystem.colors.text.primary;
  const cardMuted = teboxDesignSystem.colors.text.secondary;

  const sb = teboxDesignSystem.spacing;
  const reservationDisplayStatus = golferReservationStatusForDisplay(reservation);
  const reservationStatusHex = golferReservationStatusAccent(reservationDisplayStatus, brand.primaryColor);
  const totalIncludesCart = bookingReservationLineItemsIncludeCart(reservation);

  const vSpace = (token: string) => `calc(${token} * ${tebox.teeTimeCard.verticalCompactScale})`;
  const pad = vSpace(sb.lg);
  const dividerRhythm = vSpace(sb.md);
  const structuralBorder = tebox.colors.border;
  const cardRadius = teboxDesignSystem.borderRadius.none;
  const canLift = !muted;

  const heroNumericStyle: React.CSSProperties = {
    fontFamily: brand.fontFamily,
    fontWeight: 'var(--brand-heading-text-weight, 500)',
    fontStyle: 'var(--brand-heading-text-style, normal)',
    fontSize: tebox.teeTimeCard.heroHeaderNumericFontSize,
    lineHeight: 1.05,
    letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums',
    color: canLift ? cardText : cardMuted,
  };

  const heroSubStyle: React.CSSProperties = {
    fontFamily: brand.fontFamily,
    fontWeight: 500,
    fontSize: bodySmall.fontSize,
    lineHeight: 1.3,
    color: cardMuted,
    margin: 0,
    marginTop: vSpace(sb.xs),
  };

  const totalFootnoteStyle: React.CSSProperties = {
    fontFamily: brand.fontFamily,
    fontWeight: 500,
    fontSize: teboxDesignSystem.typography.caption.fontSize,
    lineHeight: 1.35,
    color: cardMuted,
    margin: 0,
    marginTop: vSpace(sb.xs),
  };

  const spotsChrome = bookingBrandInfoChromeStyles({
    primaryColor: brand.primaryColor,
    buttonColor: brand.primaryColor,
  });
  const spotsBadgeShellStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: sb.sm,
    padding: `${vSpace(sb.xs)} ${sb.sm}`,
    borderRadius: teboxDesignSystem.borderRadius.sm,
    ...spotsChrome,
    fontFamily: brand.fontFamily,
    fontWeight: 500,
    fontSize: bodySmall.fontSize,
    lineHeight: 1.35,
    fontVariantNumeric: 'tabular-nums',
    color: cardText,
    alignSelf: 'flex-start',
    flexShrink: 0,
    boxSizing: 'border-box',
  };

  const spotsRowStyle: React.CSSProperties = spotsBadgeShellStyle;

  return (
    <div
      className="tee-time-card-shadow"
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 400,
        /** No fixed/min height — richer layout than compact grid `TeeTimeCard`; height comes from content. */
        boxSizing: 'border-box',
        borderRadius: `var(--brand-border-radius, ${cardRadius})`,
        border: `1px solid ${structuralBorder}`,
        backgroundColor: teboxDesignSystem.colors.background.primary,
        paddingLeft: pad,
        paddingRight: pad,
        paddingTop: tebox.teeTimeCard.cardVerticalPadding,
        paddingBottom: tebox.teeTimeCard.cardVerticalPadding,
        boxShadow:
          !canLift ? 'none' : isHovered ? teboxDesignSystem.shadows.sm : 'none',
        opacity: muted ? 0.78 : 1,
        minWidth: 0,
        color: cardText,
        textAlign: 'left',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: sb.md,
          width: '100%',
          paddingBottom: dividerRhythm,
          marginBottom: dividerRhythm,
          borderBottom: `1px solid ${structuralBorder}`,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={heroNumericStyle}>{clock}</div>
          {period ? <p style={heroSubStyle}>{period}</p> : null}
        </div>
        <div
          style={{
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          <div style={heroNumericStyle}>{formatBookingReservationAmount(reservation.totalAmount ?? 0)}</div>
          <p style={totalFootnoteStyle}>{totalIncludesCart ? 'incl. Cart' : 'Total'}</p>
        </div>
      </div>

      <div
        style={{
          paddingTop: 0,
          paddingBottom: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: vSpace(sb.xs),
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontFamily: brand.fontFamily,
            fontWeight: 500,
            fontSize: tebox.teeTimeCard.reservationCardCourseTitleFontSize,
            lineHeight: teboxDesignSystem.typography.h4.lineHeight,
            color: cardText,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            margin: 0,
          }}
        >
          {reservation.course?.name || 'Unknown course'}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: sb.sm,
            rowGap: vSpace(sb.xs),
          }}
        >
          <span
            style={{
              fontFamily: brand.fontFamily,
              fontSize: bodySmall.fontSize,
              lineHeight: 1.35,
              color: cardMuted,
            }}
          >
            {dateLine}
          </span>
          <span
            {...bookingBrandInfoPillDataAttrs}
            style={getBookingBrandInfoPillStyles(reservationStatusHex)}
          >
            {golferReservationStatusLabel(reservationDisplayStatus)}
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: sb.sm,
            width: '100%',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: vSpace(sb.xs), minWidth: 0, flex: '1 1 auto' }}>
            <span
              style={spotsRowStyle}
              aria-label={spotsBadge}
              {...bookingBrandInfoPillDataAttrs}
              data-tebox-brand-info-chrome="spots-pill"
            >
              {spotsBadge}
            </span>
            {playerNamesLine ? (
              <span
                style={{
                  fontFamily: brand.fontFamily,
                  fontWeight: 500,
                  fontSize: bodySmall.fontSize,
                  lineHeight: 1.35,
                  color: cardMuted,
                  wordBreak: 'break-word',
                }}
              >
                {playerNamesLine}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            aria-label={`View details for ${reservation.course?.name || 'reservation'}`}
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(reservation);
            }}
            className="group inline-flex shrink-0 items-center gap-1 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-[var(--tebox-focus-ring)]"
            style={{
              margin: 0,
              padding: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: brand.fontFamily,
              fontSize: body.fontSize,
              lineHeight: body.lineHeight,
              fontWeight: 500,
              color: cardText,
              outlineColor: teboxDesignSystem.colors.accent.primaryDarker,
            }}
          >
            <span>View details</span>
            <svg
              className="transition-transform duration-200 ease-out group-hover:translate-x-0.5"
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M9 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Reservations as cards or guest row-cards (`layout="table"` avoids admin-style data tables). */
function BookingGolferReservationsTable({
  reservations,
  variant,
  onViewDetails,
  layout = 'cards',
  cardBrand,
  isCompactLayout = false,
}: {
  reservations: Reservation[];
  variant: 'upcoming' | 'past';
  onViewDetails: (r: Reservation) => void;
  layout?: 'cards' | 'table';
  cardBrand: GolferReservationCardBrand;
  /** When false (wide dashboard), cards render in two columns. */
  isCompactLayout?: boolean;
}) {
  const muted = variant === 'past';
  const textPrimary = muted
    ? teboxDesignSystem.colors.text.secondary
    : teboxDesignSystem.colors.text.primary;
  const textMuted = teboxDesignSystem.colors.text.secondary;
  const body = teboxDesignSystem.typography.body;

  if (layout === 'cards') {
    const gridClassName = isCompactLayout
      ? 'grid grid-cols-1 gap-3 md:gap-4 lg:gap-6'
      : 'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 md:gap-4 lg:gap-6';
    return (
      <div
        className={gridClassName}
        style={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {reservations.map((reservation) => (
          <BookingGolferReservationCard
            key={reservation.id}
            reservation={reservation}
            variant={variant}
            onViewDetails={onViewDetails}
            brand={cardBrand}
          />
        ))}
      </div>
    );
  }

  const sb = teboxDesignSystem.spacing;
  return (
    <div style={golferDashboardGuestListOuterStyle}>
      {reservations.map((reservation) => {
        const { clock, dateLine, playersLine } = bookingGolferReservationRowDisplay(reservation);
        const reservationDisplayStatus = golferReservationStatusForDisplay(reservation);
        const statusHex = golferReservationStatusAccent(reservationDisplayStatus, cardBrand.primaryColor);
        return (
          <div
            key={reservation.id}
            style={{
              ...golferDashboardGuestListRowCardStyle,
              display: 'flex',
              flexDirection: 'column',
              gap: sb.sm,
              opacity: muted ? 0.92 : 1,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: sb.md,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  ...body,
                  fontWeight: 500 as const,
                  color: textPrimary,
                  wordBreak: 'break-word',
                  minWidth: 0,
                  flex: '1 1 12rem',
                }}
              >
                {reservation.course?.name || 'Unknown course'}
              </span>
            <span
              {...bookingBrandInfoPillDataAttrs}
              style={getBookingBrandInfoPillStyles(statusHex)}
            >
                {golferReservationStatusLabel(reservationDisplayStatus)}
              </span>
            </div>
            <div
              style={{
                ...body,
                color: textMuted,
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: sb.sm,
                rowGap: sb.xs,
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{dateLine}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: textPrimary }}>{clock}</span>
            </div>
            <div style={{ ...body, color: textMuted }} title={playersLine}>
              {playersLine}
            </div>
            <div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails(reservation);
                }}
                className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{
                  ...teboxDesignSystem.components.button.secondary,
                  padding: `${sb.xs} ${sb.sm}`,
                  fontSize: body.fontSize,
                  lineHeight: body.lineHeight,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  outlineColor: teboxDesignSystem.colors.accent.primaryDarker,
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = teboxDesignSystem.colors.background.hover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = teboxDesignSystem.colors.background.primary;
                }}
              >
                View details
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BookingGolferWalletBalanceTable({
  ds,
  walletBalance,
  walletMode,
  walletAvailableCredit,
  walletCreditLimit,
  walletLedgerStatus,
}: {
  ds: BookingDashboardDs;
  walletBalance: number | null;
  walletMode: 'CREDIT' | 'PREPAID' | null;
  walletAvailableCredit: number | null;
  walletCreditLimit: number | null;
  walletLedgerStatus: string | null;
}) {
  const body = ds.typography.body;
  const font = body.fontFamily as string;
  const bal = walletBalance ?? 0;
  const balanceLabel = bal > 0 ? 'Balance Owing' : 'Credit Balance';
  const accountTypeLabel = walletMode === 'CREDIT' ? 'Credit Account' : walletMode === 'PREPAID' ? 'Prepaid Account' : '—';
  const creditLimitDisplay =
    walletMode === 'CREDIT' && walletCreditLimit != null ? `$${Number(walletCreditLimit).toFixed(2)}` : '—';
  const statusRaw = walletLedgerStatus?.trim() ?? '';
  const statusDisplay =
    statusRaw.length === 0
      ? 'Active'
      : canonicalStatusDisplayLabel(statusRaw) ?? titleCaseDashboardWords(statusRaw);
  const cardText = (ds.typography.body.color as string) || teboxDesignSystem.colors.text.primary;
  const sb = teboxDesignSystem.spacing;
  const h3Title = {
    fontSize: teboxDesignSystem.typography.h4.fontSize,
    fontWeight: 500 as const,
    lineHeight: '21px',
    letterSpacing: 'normal' as const,
  };
  const statusNorm = statusDisplay.toLowerCase();
  const ledgerPillHex =
    statusNorm === 'active' || statusNorm === 'open'
      ? (String(ds.colors.primary || '').trim() || BOOKING_RESERVED_ACTIVE_BG)
      : pc.neutral;

  return (
    <div
      style={{
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: ds.borderRadius.borderedShell,
        border: `1px solid ${teboxDesignSystem.colors.border.light}`,
        backgroundColor: teboxDesignSystem.colors.background.primary,
        boxShadow: teboxDesignSystem.shadows.sm,
        overflow: 'hidden',
        marginBottom: ds.spacing.lg,
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: sb.lg,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: sb.md,
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <h3
              style={{
                margin: 0,
                marginBottom: sb.sm,
                fontFamily: font,
                fontWeight: h3Title.fontWeight,
                fontSize: h3Title.fontSize,
                lineHeight: h3Title.lineHeight,
                letterSpacing: h3Title.letterSpacing,
                color: cardText,
                wordBreak: 'break-word',
              }}
            >
              Wallet Account
            </h3>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: sb.md,
                rowGap: sb.xs,
              }}
            >
              <span
                style={{
                  fontFamily: font,
                  fontSize: body.fontSize,
                  lineHeight: body.lineHeight,
                  color: cardText,
                }}
              >
                {accountTypeLabel}
              </span>
              <span
                {...bookingBrandInfoPillDataAttrs}
                style={getBookingBrandInfoPillStyles(ledgerPillHex)}
              >
                {statusDisplay}
              </span>
              {walletMode === 'CREDIT' ? (
                <span
                  style={{
                    fontFamily: font,
                    fontSize: body.fontSize,
                    lineHeight: body.lineHeight,
                    color: cardText,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  Limit {creditLimitDisplay}
                </span>
              ) : null}
            </div>
          </div>
          <div
            style={{
              flex: '0 0 auto',
              textAlign: 'right',
              fontFamily: font,
              color: cardText,
            }}
          >
            <div
              style={{
                fontSize: body.fontSize,
                lineHeight: body.lineHeight,
                fontWeight: 400,
                marginBottom: sb.xs,
              }}
            >
              {balanceLabel}
            </div>
            <div style={{ fontVariantNumeric: 'tabular-nums' }}>
              {bookingDashboardCurrencyTotalSm(Math.abs(bal), ds)}
            </div>
            {walletMode === 'CREDIT' && walletAvailableCredit !== null ? (
              <div
                style={{
                  ...ds.typography.caption,
                  color: cardText,
                  marginTop: sb.xs,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                Available credit: ${walletAvailableCredit.toFixed(2)}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function BookingGolferWalletTransactionsStacked({
  transactions,
  debitAccentColor,
}: {
  transactions: Array<{ description: string; date: string; type?: string; amount: number }>;
  debitAccentColor: string;
}) {
  const body = teboxDesignSystem.typography.body;
  return (
    <div style={golferDashboardGuestListOuterStyle}>
      {transactions.map((transaction, index) => {
        const d = new Date(transaction.date);
        let dateLine = '—';
        let clock = '—';
        try {
          dateLine = format(d, 'MM/dd/yy');
          clock = formatBookingClockTime(d);
        } catch {
          /* keep fallbacks */
        }
        const amtSign = transaction.type === 'DEBIT' ? '-' : '+';
        const amtColor =
          transaction.type === 'DEBIT' ? debitAccentColor : teboxDesignSystem.colors.text.secondary;
        return (
          <div
            key={index}
            style={{
              ...golferDashboardGuestListRowCardStyle,
              display: 'flex',
              flexDirection: 'column',
              gap: teboxDesignSystem.spacing.xs,
            }}
          >
            <div
              style={{
                ...body,
                color: teboxDesignSystem.colors.text.primary,
                fontWeight: 500 as const,
                wordBreak: 'break-word',
              }}
            >
              {transaction.description}
            </div>
            <div
              style={{
                ...body,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: teboxDesignSystem.spacing.sm,
                color: teboxDesignSystem.colors.text.secondary,
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>
                {dateLine} ·{' '}
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{clock}</span>
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: amtColor, fontWeight: 500 }}>
                {amtSign}${transaction.amount.toFixed(2)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BookingGolferWalletTransactionsGuestList({
  transactions,
  debitAccentColor,
}: {
  transactions: Array<{ description: string; date: string; type?: string; amount: number }>;
  /** Club / booking primary (e.g. `designSystem.colors.primary`) for debit line amounts. */
  debitAccentColor: string;
}) {
  return (
    <BookingGolferWalletTransactionsStacked
      transactions={transactions}
      debitAccentColor={debitAccentColor}
    />
  );
}

function BookingGolferMembershipOverviewCard({
  ds,
  membership,
}: {
  ds: BookingDashboardDs;
  membership: MembershipSubscription;
}) {
  const body = ds.typography.body;
  const font = body.fontFamily as string;
  const totalPrice = Number(membership.product.price);
  const priceForDisplay = Number.isFinite(totalPrice) ? totalPrice : 0;
  const priceStr = priceForDisplay.toFixed(2);
  const cardText = (ds.typography.body.color as string) || teboxDesignSystem.colors.text.primary;
  const sb = teboxDesignSystem.spacing;
  const h3Title = {
    fontSize: teboxDesignSystem.typography.h4.fontSize,
    fontWeight: 500 as const,
    lineHeight: '21px',
    letterSpacing: 'normal' as const,
  };

  let startShort = '—';
  let endShort = 'No end date';
  try {
    startShort = format(new Date(membership.startDate), 'MM/dd/yy');
  } catch {
    /* keep fallback */
  }
  try {
    if (membership.endDate) endShort = format(new Date(membership.endDate), 'MM/dd/yy');
  } catch {
    /* keep fallback */
  }

  const mem = String(membership.status ?? '')
    .trim()
    .toUpperCase();
  const membershipStatusHex =
    mem === 'ACTIVE'
      ? (String(ds.colors.primary || '').trim() || BOOKING_RESERVED_ACTIVE_BG)
      : ['CANCELLED', 'CANCELED', 'EXPIRED'].includes(mem)
        ? golferStatusPill.cancelledReservation
        : ['PENDING', 'PAST_DUE'].includes(mem)
          ? golferStatusPill.pendingReservation
          : pc.warning;

  const statusPill = (
    <span
      {...bookingBrandInfoPillDataAttrs}
      style={getBookingBrandInfoPillStyles(membershipStatusHex)}
    >
      {membership.status === 'ACTIVE'
        ? 'Active'
        : canonicalStatusDisplayLabel(membership.status) ?? titleCaseDashboardWords(membership.status)}
    </span>
  );

  return (
    <div
      style={{
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: ds.borderRadius.borderedShell,
        border: `1px solid ${teboxDesignSystem.colors.border.light}`,
        backgroundColor: teboxDesignSystem.colors.background.primary,
        boxShadow: teboxDesignSystem.shadows.sm,
        overflow: 'hidden',
        marginBottom: ds.spacing.lg,
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: sb.lg,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: sb.md,
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <h3
              style={{
                margin: 0,
                marginBottom: sb.sm,
                fontFamily: font,
                fontWeight: h3Title.fontWeight,
                fontSize: h3Title.fontSize,
                lineHeight: h3Title.lineHeight,
                letterSpacing: h3Title.letterSpacing,
                color: cardText,
                wordBreak: 'break-word',
              }}
            >
              Membership overview
            </h3>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: sb.md,
                rowGap: sb.xs,
              }}
            >
              <span
                style={{
                  fontFamily: font,
                  fontSize: body.fontSize,
                  lineHeight: body.lineHeight,
                  color: cardText,
                }}
              >
                {membership.product.name}
              </span>
              {statusPill}
              <span
                style={{
                  fontFamily: font,
                  fontSize: body.fontSize,
                  lineHeight: body.lineHeight,
                  color: cardText,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                Total ${priceStr}
              </span>
              <span
                style={{
                  fontFamily: font,
                  fontSize: body.fontSize,
                  lineHeight: body.lineHeight,
                  color: cardText,
                }}
              >
                {membership.product.billingType}
              </span>
            </div>
          </div>
          <div
            style={{
              flex: '0 0 auto',
              textAlign: 'right',
              fontFamily: font,
              fontSize: body.fontSize,
              lineHeight: body.lineHeight,
              color: cardText,
            }}
          >
            <div>Started {startShort}</div>
            <div style={{ marginTop: sb.xs }}>
              {membership.endDate ? `Through ${endShort}` : 'No end date'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function golferPaymentSchedulePayNowButton(
  ds: BookingDashboardDs,
  primaryButtonTextColor: string,
  payment: PaymentSchedule,
  onExpiredPaymentLink?: () => void,
): React.ReactNode {
  if (payment.status !== 'PENDING' || !payment.paymentLinks || payment.paymentLinks.length === 0)
    return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        const paymentLink = payment.paymentLinks![0];
        if (paymentLink && new Date(paymentLink.expiresAt) > new Date()) {
          window.open(paymentLink.url, '_blank');
        } else {
          onExpiredPaymentLink?.();
        }
      }}
      style={{
        backgroundColor: ds.colors.primary,
        color: primaryButtonTextColor,
        border: 'none',
        borderRadius: teboxDesignSystem.borderRadius.sm,
        padding: `${teboxDesignSystem.spacing.xs} ${teboxDesignSystem.spacing.sm}`,
        fontSize: teboxDesignSystem.components.pill.sizes.small.fontSize,
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      Pay Now
    </button>
  );
}

function BookingGolferPaymentScheduleStacked({
  ds,
  payments,
  primaryButtonTextColor,
  onExpiredPaymentLink,
}: {
  ds: BookingDashboardDs;
  payments: PaymentSchedule[];
  primaryButtonTextColor: string;
  onExpiredPaymentLink?: () => void;
}) {
  const body = teboxDesignSystem.typography.body;
  return (
    <div style={golferDashboardGuestListOuterStyle}>
      {payments.map((payment) => {
        const due = format(new Date(payment.dueDate), 'MM/dd/yy');
        const paid =
          payment.paidAt != null ? format(new Date(payment.paidAt), 'MM/dd/yy') : null;
        const payBtn = golferPaymentSchedulePayNowButton(
          ds,
          primaryButtonTextColor,
          payment,
          onExpiredPaymentLink,
        );
        return (
          <div
            key={payment.id}
            style={{
              ...golferDashboardGuestListRowCardStyle,
              display: 'flex',
              flexDirection: 'column',
              gap: teboxDesignSystem.spacing.sm,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: teboxDesignSystem.spacing.sm,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ ...body, fontWeight: 500, color: teboxDesignSystem.colors.text.primary }}>
                Installment #{payment.installmentNumber}
              </span>
              <span
                {...bookingBrandInfoPillDataAttrs}
                style={getBookingBrandInfoPillStyles(golferInstallmentStatusAccent(payment.status))}
              >
                {golferInstallmentStatusLabel(payment.status)}
              </span>
            </div>
            <div
              style={{
                ...body,
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: teboxDesignSystem.spacing.md,
                rowGap: teboxDesignSystem.spacing.xs,
                color: teboxDesignSystem.colors.text.secondary,
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>Due {due}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>${payment.amount}</span>
              <span style={{ whiteSpace: 'nowrap' }}>Paid {paid ?? '—'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              {payBtn ?? (
                <span style={{ ...body, color: teboxDesignSystem.colors.text.tertiary }}>—</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BookingGolferPaymentScheduleTable({
  ds,
  payments,
  primaryButtonTextColor,
  onExpiredPaymentLink,
}: {
  ds: BookingDashboardDs;
  payments: PaymentSchedule[];
  primaryButtonTextColor: string;
  onExpiredPaymentLink?: () => void;
}) {
  return (
    <BookingGolferPaymentScheduleStacked
      ds={ds}
      payments={payments}
      primaryButtonTextColor={primaryButtonTextColor}
      onExpiredPaymentLink={onExpiredPaymentLink}
    />
  );
}

const GOLFER_DASHBOARD_NAV_ICON_PX = 24;

/** Admin `CustomIcons` glyphs — stroke matches staff UI defaults. */
function GolferDashboardNavSectionIcon({ label, color }: { label: string; color: string }) {
  const size = GOLFER_DASHBOARD_NAV_ICON_PX;
  const strokeWidth = ICON_STROKE.standard;
  switch (label) {
    case 'Reservations':
      return <BookingsIcon size={size} color={color} strokeWidth={strokeWidth} />;
    case 'Wallet':
      return <WalletIcon size={size} color={color} strokeWidth={strokeWidth} />;
    case 'Gift cards':
      return <GiftCardIcon size={size} color={color} strokeWidth={strokeWidth} />;
    case 'Passes':
      return <PassesIcon size={size} color={color} strokeWidth={strokeWidth} />;
    case 'Membership':
      return <CustomersIcon size={size} color={color} strokeWidth={strokeWidth} />;
    case 'Account':
      return <ContactIcon size={size} color={color} strokeWidth={strokeWidth} />;
    default:
      return <BookingsIcon size={size} color={color} strokeWidth={strokeWidth} />;
  }
}

const GolferDashboard: React.FC<GolferDashboardProps> = ({
  onBackToBooking,
  brandColors,
  omitWelcomeHeading = false,
}) => {
  const tebox = useBookingTebox();
  const [activeTab, setActiveTab] = useState(0);
  const [openTabDropdown, setOpenTabDropdown] = useState(false);
  const { compact: isCompact, phone: isPhoneDashboard } = useGolferDashboardViewport();
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [membershipData, setMembershipData] = useState<MembershipSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [receiptDetail, setReceiptDetail] = useState<Reservation | null>(null);
  const [receiptDetailLoading, setReceiptDetailLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editNotesDraft, setEditNotesDraft] = useState('');
  const [selfServiceBusy, setSelfServiceBusy] = useState(false);
  const [selfServiceModalError, setSelfServiceModalError] = useState<string | null>(null);
  const [partnerInviteUrlDash, setPartnerInviteUrlDash] = useState<string | null>(null);
  const [partnerInviteBusyDash, setPartnerInviteBusyDash] = useState(false);
  const [partnerInviteErrDash, setPartnerInviteErrDash] = useState<string | null>(null);
  const [clubPolicies, setClubPolicies] = useState<string>('');
  const [clubContactPhone, setClubContactPhone] = useState('');
  const [cancellationWindowHours, setCancellationWindowHours] = useState(2);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    preferences: '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [walletAvailableCredit, setWalletAvailableCredit] = useState<number | null>(null);
  const [walletCreditLimit, setWalletCreditLimit] = useState<number | null>(null);
  const [walletMode, setWalletMode] = useState<'CREDIT' | 'PREPAID' | null>(null);
  const [walletLedgerStatus, setWalletLedgerStatus] = useState<string | null>(null);
  const [walletLedgerId, setWalletLedgerId] = useState<string | null>(null);
  const [walletMobilePassAccessToken, setWalletMobilePassAccessToken] = useState<string | null>(null);
  const [walletTransactions, setWalletTransactions] = useState<any[]>([]);
  const [customerPasses, setCustomerPasses] = useState<any[]>([]);
  const [passesLoading, setPassesLoading] = useState(false);
  const [customerGiftCards, setCustomerGiftCards] = useState<GiftCardSummary[]>([]);
  const [giftCardsLoading, setGiftCardsLoading] = useState(false);
  const [paymentLinkExpiredMessage, setPaymentLinkExpiredMessage] = useState<string | null>(null);

  // Design system constants - use brandColors if provided, otherwise defaults
  const defaultBrandColors: BrandColors = {
    primaryColor: '#000000',
    secondaryColor: tebox.colors.textSecondary,
    textColor: tebox.colors.textPrimary,
    fontFamily: 'Inter',
    buttonColor: '#000000',
    buttonTextColor: '#FFFFFF',
    primaryTextColor: '#FFFFFF',
    accentColor: '#000000',
  };
  
  const activeBrandColors = brandColors || defaultBrandColors;

  /** Use CSS var so typography tracks admin brand changes as soon as `applyBrandSettingsSync` runs, not only after React `setBrandColors`. */
  const brandFontFamily = 'var(--brand-font)';

  const designSystem = {
    colors: {
      primary: activeBrandColors.primaryColor,
      white: '#FFFFFF',
      gray: {
        50: tebox.colors.bgHighlight,
        100: tebox.colors.bgSecondary,
        200: tebox.colors.border,
        300: tebox.colors.border,
        400: tebox.colors.textTertiary,
        500: activeBrandColors.secondaryColor,
        600: tebox.colors.textSecondary,
        700: tebox.colors.textSecondary,
        800: activeBrandColors.textColor,
        900: activeBrandColors.textColor,
      }
    },
    typography: {
      h1: {
        fontFamily: brandFontFamily,
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: teboxDesignSystem.typography.h3.fontSize,
        lineHeight: '28px',
        letterSpacing: tebox.typography.h3.letterSpacing,
        color: activeBrandColors.textColor
      },
      /** Aligns with guest booking section titles (e.g. “Available Times”): brand text, 700, 24/20 by breakpoint. */
      h2: {
        fontFamily: brandFontFamily,
        fontStyle: 'normal',
        fontWeight: '500',
        fontSize: isCompact ? '20px' : '24px',
        lineHeight: '21px',
        letterSpacing: 'normal',
        color: activeBrandColors.textColor
      },
      body: {
        fontFamily: brandFontFamily,
        fontStyle: 'normal',
        fontWeight: '400',
        fontSize: teboxDesignSystem.typography.body.fontSize,
        lineHeight: '16px',
        letterSpacing: '0px',
        color: activeBrandColors.textColor
      },
      caption: {
        fontFamily: brandFontFamily,
        fontStyle: 'normal',
        fontWeight: '400',
        fontSize: teboxDesignSystem.typography.body.fontSize,
        lineHeight: '16px',
        letterSpacing: '0px',
        color: activeBrandColors.secondaryColor
      }
    },
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px'
    },
    borderRadius: {
      sm: 'var(--brand-border-radius, var(--tebox-radius-container, 6px))',
      md: 'var(--brand-border-radius, var(--tebox-radius-container, 6px))',
      lg: 'var(--brand-border-radius, var(--tebox-radius-container, 6px))',
      /** Bordered cards / shells — soft Tebox container radius. */
      borderedShell: 'var(--brand-border-radius, var(--tebox-radius-container, 6px))',
    }
  };

  const communicationPrefs = useMemo(
    () => ({
      ...DEFAULT_COMMUNICATION_PREFS,
      ...customer?.communicationPreferences,
    }),
    [customer?.communicationPreferences]
  );

  const golferDashboardTabs = useMemo(
    () => [
      { id: 0, label: 'Reservations' },
      { id: 1, label: 'Wallet' },
      { id: 2, label: 'Gift cards' },
      { id: 3, label: 'Passes' },
      ...(isMember(customer) ? [{ id: 4, label: 'Membership' }] : []),
      { id: isMember(customer) ? 5 : 4, label: 'Account' },
    ],
    [customer]
  );

  const prevIsMemberRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    let nextTab: number | null = null;

    if (!customer) {
      prevIsMemberRef.current = undefined;
    } else {
      const memberNow = isMember(customer);
      const prev = prevIsMemberRef.current;

      if (prev !== undefined && prev !== memberNow) {
        if (!prev && memberNow && activeTab === 4) {
          nextTab = 5;
        } else if (prev && !memberNow) {
          if (activeTab === 5) nextTab = 4;
          else if (activeTab === 4) nextTab = 0;
        }
      }

      if (prev === undefined) {
        prevIsMemberRef.current = memberNow;
      } else if (prev !== memberNow) {
        prevIsMemberRef.current = memberNow;
      }
    }

    if (nextTab !== null) {
      setActiveTab(nextTab);
      return;
    }

    const valid = new Set(golferDashboardTabs.map((t) => t.id));
    if (!valid.has(activeTab)) {
      setActiveTab(0);
    }
  }, [customer, golferDashboardTabs, activeTab]);

  useEffect(() => {
    setPaymentLinkExpiredMessage(null);
  }, [activeTab]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    const refresh = () => {
      void loadDashboardData();
    };
    window.addEventListener('reservation-created', refresh);
    window.addEventListener('reservation-updated', refresh);
    return () => {
      window.removeEventListener('reservation-created', refresh);
      window.removeEventListener('reservation-updated', refresh);
    };
  }, []);

  // Close tab dropdown when clicking outside (compact / tablet layout)
  useEffect(() => {
    if (!isCompact) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (openTabDropdown && !target.closest('[data-tab-dropdown]')) {
        setOpenTabDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCompact, openTabDropdown]);

  useEffect(() => {
    if (!receiptModalOpen || !selectedReservation?.id) {
      setReceiptDetail(null);
      setReceiptDetailLoading(false);
      return;
    }
    let cancelled = false;
    setReceiptDetail(null);
    setReceiptDetailLoading(true);
    reservationsService
      .getReservation(selectedReservation.id, true)
      .then((r) => {
        if (!cancelled && r) setReceiptDetail(r as Reservation);
      })
      .catch(() => {
        if (!cancelled) setReceiptDetail(null);
      })
      .finally(() => {
        if (!cancelled) setReceiptDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [receiptModalOpen, selectedReservation?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Get current customer
      const currentCustomer = await customerAuthService.getCurrentUser();
      if (!currentCustomer) {
        setError('Please log in to view your dashboard');
        return;
      }

      const profileCustomer = currentCustomer as CustomerProfile;
      setCustomer(profileCustomer);

      // Load reservations via customer JWT (booker + joined player slots)
      try {
        const customerReservationsResponse = await reservationsService.getMyReservations(
          1,
          1000,
          undefined,
          true
        );
        setReservations(customerReservationsResponse.reservations);
      } catch (reservationErr) {
        console.error('Error loading golfer reservations:', reservationErr);
        setReservations([]);
        setError('Could not load your reservations. Please refresh the page or sign in again.');
      }

      // Load membership data if customer is a member
      if (isMember(profileCustomer)) {
        try {
          console.log('🔍 Loading membership data for member:', profileCustomer.id);
          const membershipData = await membershipService.getActiveMembership(profileCustomer.id);
          if (membershipData) {
            console.log('✅ Found membership data:', membershipData);
            setMembershipData(membershipData);
          } else {
            console.log('ℹ️ No active membership found for member:', currentCustomer.id);
            setMembershipData(null);
          }
        } catch (err) {
          console.error('❌ Error loading membership data:', err);
          // Don't set error for membership data failure, just log it
          setMembershipData(null);
        }
      }

      // Load club policies (public API — customers are not authorized for GET /golf-clubs staff list)
      try {
        console.log('🔍 Loading club policies...');
        const clubKey =
          profileCustomer.clubId ||
          (profileCustomer.clubNumber != null ? String(profileCustomer.clubNumber) : null);
        if (clubKey) {
          const courses = await golfClubsService.getCoursesWithTemplates(clubKey);
          const courseWithClub = courses.find((c) => c.club && c.club.name);
          if (courseWithClub?.club) {
            const club = courseWithClub.club;
            if (club.policies) {
              console.log('Found club policies:', club.policies);
              setClubPolicies(club.policies);
            }
            if (club.phone) setClubContactPhone(club.phone);
            const windowH =
              club.cancellationWindowHours ?? club.cancellationPolicyHours ?? 2;
            setCancellationWindowHours(windowH);
          }
        } else {
          const clubs = await golfClubsService.getClubInfo();
          if (clubs.length > 0) {
            const club = clubs[0];
            if (club.policies) setClubPolicies(club.policies);
            if (club.phone) setClubContactPhone(club.phone);
            const windowH =
              club.cancellationWindowHours ?? club.cancellationPolicyHours ?? 2;
            setCancellationWindowHours(windowH);
          }
        }
      } catch (policiesError) {
        console.error('❌ Error loading club policies:', policiesError);
        // Don't set error for policies issues, just log them
      }

      // Load wallet data
      try {
        console.log('🔍 Loading wallet data for customer:', profileCustomer.id);
        // Fetch wallet summary (balance, available credit, etc.)
        const walletSummary = await walletApi.getCustomerSummary(profileCustomer.id);
        console.log('✅ Wallet summary loaded:', walletSummary);
        
        if (walletSummary.ledger) {
          setWalletLedgerId(walletSummary.ledger.id || null);
          setWalletMobilePassAccessToken(walletSummary.ledger.mobilePassAccessToken ?? null);
          setWalletBalance(walletSummary.ledger.balance || 0);
          setWalletAvailableCredit(walletSummary.ledger.availableCredit ?? null);
          setWalletCreditLimit(walletSummary.ledger.creditLimit ?? null);
          setWalletMode(walletSummary.ledger.mode || null);
          setWalletLedgerStatus(walletSummary.ledger.status ?? null);
          console.log('✅ Wallet details:', {
            balance: walletSummary.ledger.balance,
            availableCredit: walletSummary.ledger.availableCredit,
            creditLimit: walletSummary.ledger.creditLimit,
            mode: walletSummary.ledger.mode
          });
        } else {
          // No wallet account
          setWalletLedgerId(null);
          setWalletMobilePassAccessToken(null);
          setWalletBalance(0);
          setWalletAvailableCredit(null);
          setWalletCreditLimit(null);
          setWalletMode(null);
          setWalletLedgerStatus(null);
        }
        
        // Fetch wallet transactions - request a high limit to get all transactions
        const walletTransactions = await walletApi.getCustomerTransactions(profileCustomer.id, 1000);
        console.log('✅ Wallet transactions loaded:', walletTransactions.length, 'transactions');
        setWalletTransactions(walletTransactions);
        
      } catch (walletError) {
        console.error('❌ Error loading wallet data:', walletError);
        // Don't set error for wallet issues, just log them
        setWalletBalance(0);
        setWalletAvailableCredit(null);
        setWalletCreditLimit(null);
        setWalletMode(null);
        setWalletLedgerStatus(null);
        setWalletLedgerId(null);
        setWalletMobilePassAccessToken(null);
        setWalletTransactions([]);
      }

      try {
        setGiftCardsLoading(true);
        const giftCards = await giftCardsService.getMyGiftCards();
        setCustomerGiftCards(Array.isArray(giftCards) ? giftCards : []);
      } catch (giftCardErr) {
        console.error('Error loading gift cards:', giftCardErr);
        setCustomerGiftCards([]);
      } finally {
        setGiftCardsLoading(false);
      }

      try {
        setPassesLoading(true);
        const passes = await passesService.getMyPasses();
        setCustomerPasses(Array.isArray(passes) ? passes : []);
      } catch (passErr) {
        console.error('Error loading passes:', passErr);
        setCustomerPasses([]);
      } finally {
        setPassesLoading(false);
      }

      const displayFirstName =
        profileCustomer.firstName?.split('.')[0]?.split(' ')[0] || profileCustomer.firstName || '';
      setProfileForm({
        ...profileFormFromCustomer(profileCustomer),
        firstName: displayFirstName,
      });
      setProfileMessage(null);

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelContact = () => {
    if (customer) {
      const displayFirstName =
        customer.firstName?.split('.')[0]?.split(' ')[0] || customer.firstName || '';
      setProfileForm({
        ...profileFormFromCustomer(customer),
        firstName: displayFirstName,
      });
    }
    setProfileMessage(null);
  };

  const handleSaveContact = async () => {
    setProfileMessage(null);
    setProfileSaving(true);
    try {
      const result = await customerAuthService.updateProfile({
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        email: profileForm.email,
        phone: profileForm.phone,
        dateOfBirth: profileForm.dateOfBirth.trim() === '' ? null : profileForm.dateOfBirth,
        preferences: profileForm.preferences,
      });
      if (result.ok) {
        const next = result.customer as CustomerProfile;
        setCustomer(next);
        const displayFirstName =
          next.firstName?.split('.')[0]?.split(' ')[0] || next.firstName || '';
        setProfileForm({
          ...profileFormFromCustomer(next),
          firstName: displayFirstName,
        });
        setProfileMessage({ type: 'ok', text: 'Profile saved.' });
      } else {
        setProfileMessage({ type: 'err', text: result.error });
      }
    } catch (e) {
      setProfileMessage({
        type: 'err',
        text: e instanceof Error ? e.message : 'Could not save profile',
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleViewDetails = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setReceiptModalOpen(true);
  };

  const closeReceiptModal = () => {
    setReceiptModalOpen(false);
    setSelectedReservation(null);
    setReceiptDetail(null);
    setReceiptDetailLoading(false);
  };

  const isUpcomingReservation = (reservation: Reservation) => {
    const status = reservation.status?.toLowerCase() ?? '';
    if (['canceled', 'cancelled', 'completed'].includes(status)) return false;
    const play = getBookingReservationPlayDate(reservation);
    if (!play) return true;
    return play.getTime() >= Date.now();
  };

  const isBookerForReservation = (reservation: Reservation) =>
    !!customer && !!reservation.customerId && reservation.customerId === customer.id;

  const canEditReservation = (reservation: Reservation) => {
    if (!isBookerForReservation(reservation)) return false;
    if (!isUpcomingReservation(reservation)) return false;
    const play = getBookingReservationPlayDate(reservation);
    const now = new Date();
    const hoursUntilReservation = play
      ? (play.getTime() - now.getTime()) / (1000 * 60 * 60)
      : Number.POSITIVE_INFINITY;
    return hoursUntilReservation > cancellationWindowHours;
  };

  const canCancelReservation = (reservation: Reservation) => {
    if (!isBookerForReservation(reservation)) return false;
    if (!isUpcomingReservation(reservation)) return false;
    const play = getBookingReservationPlayDate(reservation);
    const now = new Date();
    const hoursUntilReservation = play
      ? (play.getTime() - now.getTime()) / (1000 * 60 * 60)
      : Number.POSITIVE_INFINITY;
    return hoursUntilReservation > cancellationWindowHours;
  };

  const handleEditReservation = () => {
    if (selectedReservation) setEditNotesDraft(selectedReservation.notes || '');
    setSelfServiceModalError(null);
    setPartnerInviteUrlDash(null);
    setPartnerInviteErrDash(null);
    setPartnerInviteBusyDash(false);
    setShowEditModal(true);
  };

  const handleCancelReservation = () => {
    setSelfServiceModalError(null);
    setShowCancelModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelfServiceModalError(null);
    setPartnerInviteUrlDash(null);
    setPartnerInviteErrDash(null);
    setPartnerInviteBusyDash(false);
  };

  const handleMintPartnerInviteDash = useCallback(async () => {
    if (!selectedReservation) return;
    const token = customerAuthService.getAccessToken();
    if (!token) {
      setPartnerInviteErrDash('Sign in required.');
      return;
    }
    setPartnerInviteBusyDash(true);
    setPartnerInviteErrDash(null);
    try {
      apiClient.setAuthToken(token);
      const res = await apiClient.post<{ success: boolean; inviteUrl: string; expiresAt: string }>(
        `/customer-auth/reservations/${encodeURIComponent(selectedReservation.id)}/partner-invite`,
        {}
      );
      if (res.success && res.inviteUrl) {
        setPartnerInviteUrlDash(res.inviteUrl);
      } else {
        setPartnerInviteErrDash('Could not create invite link.');
      }
    } catch (e: unknown) {
      setPartnerInviteErrDash(e instanceof Error ? e.message : 'Could not create invite link.');
    } finally {
      setPartnerInviteBusyDash(false);
    }
  }, [selectedReservation]);

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setSelfServiceModalError(null);
  };

  const handleSaveReservationNotes = async () => {
    if (!selectedReservation) return;
    setSelfServiceBusy(true);
    setSelfServiceModalError(null);
    const result = await customerAuthService.updateMyReservationNotes(
      selectedReservation.id,
      editNotesDraft
    );
    setSelfServiceBusy(false);
    if (!result.ok) {
      setSelfServiceModalError(result.error);
      return;
    }
    setReservations((prev) =>
      prev.map((r) => (r.id === selectedReservation.id ? { ...r, notes: editNotesDraft } : r))
    );
    setSelectedReservation((r) => (r ? { ...r, notes: editNotesDraft } : null));
    closeEditModal();
  };

  const handleConfirmCancelReservation = async () => {
    if (!selectedReservation) return;
    setSelfServiceBusy(true);
    setSelfServiceModalError(null);
    const result = await customerAuthService.cancelMyReservation(selectedReservation.id);
    setSelfServiceBusy(false);
    if (!result.ok) {
      setSelfServiceModalError(result.error);
      return;
    }
    const cid = customer?.id ?? selectedReservation.customerId;
    if (cid) {
      reservationsService.invalidateCustomerReservationMutation(cid, selectedReservation.id);
    }
    await loadDashboardData();
    closeCancelModal();
    setReceiptModalOpen(false);
    setSelectedReservation(null);
  };

  const upcomingReservations = reservations.filter((r) => {
    const status = r.status?.toLowerCase() ?? '';
    if (['canceled', 'cancelled', 'completed'].includes(status)) return false;
    const play = getBookingReservationPlayDate(r);
    if (!play) return true;
    return play.getTime() >= Date.now();
  });

  const pastReservations = reservations.filter((r) => {
    const status = r.status?.toLowerCase() ?? '';
    if (['canceled', 'cancelled', 'completed'].includes(status)) return true;
    const play = getBookingReservationPlayDate(r);
    if (!play) return false;
    return play.getTime() < Date.now();
  });

  const receiptDisplayReservation =
    receiptModalOpen && selectedReservation ? (receiptDetail ?? selectedReservation) : null;

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        backgroundColor: tebox.colors.bgPrimary
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: `2px solid ${tebox.colors.border}`,
          borderTop: `2px solid ${tebox.colors.focusRing}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: designSystem.spacing.lg,
        backgroundColor: tebox.colors.bgPrimary,
        minHeight: '100vh',
        boxSizing: 'border-box'
      }}>
        <div style={{
          padding: designSystem.spacing.md,
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: designSystem.borderRadius.md,
          marginBottom: designSystem.spacing.md
        }}>
          <div style={{
            ...designSystem.typography.body,
            color: '#dc2626'
          }}>
            {error}
          </div>
        </div>
        <button
          onClick={onBackToBooking}
          style={{
            backgroundColor: 'transparent',
            color: designSystem.colors.primary,
            border: `1px solid ${designSystem.colors.primary}`,
            borderRadius: designSystem.borderRadius.md,
            padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
            fontSize: teboxDesignSystem.typography.body.fontSize,
            fontWeight: '500',
            cursor: 'pointer',
            fontFamily: designSystem.typography.body.fontFamily
          }}
        >
          Back to Booking
        </button>
      </div>
    );
  }

  const welcomeShortName =
    customer?.firstName?.split('.')[0]?.split(' ')[0] || customer?.firstName || 'Guest';

  /** Phones: narrower gutter + safe-area; tablets/desktop use page inset + safe-area. */
  const dashboardHorizInset = isPhoneDashboard
    ? tebox.spacing.stackGap
    : tebox.spacing.pageInset;
  const dashboardInsetTopBottom = `${tebox.spacing.stackGap}`;
  const dashboardInsetLeft = `max(${dashboardHorizInset}, env(safe-area-inset-left, 0px))`;
  const dashboardInsetRight = `max(${dashboardHorizInset}, env(safe-area-inset-right, 0px))`;

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: tebox.colors.bgPrimary,
        fontFamily: designSystem.typography.body.fontFamily,
        minHeight: '100vh',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        minWidth: 0,
      }}
    >
      {/* Full-width sticky header */}
      <div
        style={{
          paddingTop: dashboardInsetTopBottom,
          paddingBottom: dashboardInsetTopBottom,
          paddingLeft: dashboardInsetLeft,
          paddingRight: dashboardInsetRight,
          backgroundColor: tebox.colors.bgPrimary,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          minHeight: 'auto',
          boxSizing: 'border-box',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: isCompact ? tebox.spacing.stackGapTight : tebox.spacing.stackGap,
          borderBottom: `1px solid ${tebox.colors.border}`,
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: isPhoneDashboard ? 'column' : 'row',
            alignItems: isPhoneDashboard ? 'stretch' : 'center',
            justifyContent: isPhoneDashboard ? 'flex-start' : 'space-between',
            flexWrap: isPhoneDashboard ? 'nowrap' : 'wrap',
            rowGap: tebox.spacing.stackGapTight,
            columnGap: tebox.spacing.stackGap,
          }}
        >
          {!omitWelcomeHeading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tebox.spacing.stackGap,
              minWidth: 0,
              flex: isPhoneDashboard ? undefined : '0 1 auto',
              width: isPhoneDashboard ? '100%' : undefined,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, width: '100%' }}>
              <h1
                style={{
                  ...tebox.typography.h3,
                  fontFamily: designSystem.typography.body.fontFamily,
                  color: activeBrandColors.textColor,
                  display: 'block',
                  margin: 0,
                  lineHeight: tebox.typography.h3.lineHeight,
                  ...(isPhoneDashboard
                    ? {
                        fontSize: teboxDesignSystem.typography.h4.fontSize,
                        lineHeight: teboxDesignSystem.typography.h4.lineHeight,
                        letterSpacing: 'normal',
                        fontWeight: 500 as const,
                      }
                    : {}),
                }}
              >
                {`Hello, ${welcomeShortName}`}
              </h1>
            </div>
          </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tebox.spacing.stackGap,
              flexShrink: isPhoneDashboard ? undefined : 0,
              flex: isPhoneDashboard ? undefined : '0 0 auto',
              alignSelf: isPhoneDashboard ? 'stretch' : undefined,
              width: isPhoneDashboard ? '100%' : undefined,
              flexDirection: isPhoneDashboard ? 'column' : 'row',
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              onClick={onBackToBooking}
              style={{
                backgroundColor: designSystem.colors.primary,
                color: activeBrandColors.primaryTextColor,
                border: 'none',
                borderRadius: designSystem.borderRadius.md,
                padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                fontSize: tebox.typography.body.fontSize,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: designSystem.typography.body.fontFamily,
                textTransform: 'none',
                transition: 'filter 0.15s ease',
                width: isPhoneDashboard ? '100%' : undefined,
                minHeight: isPhoneDashboard ? 48 : undefined,
                boxSizing: 'border-box',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.filter = 'brightness(0.92)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.filter = 'none';
              }}
            >
              Book a tee time
            </button>
          </div>
        </div>

        {isCompact ? (
          <div style={{ width: '100%', maxWidth: '100%' }}>
            <div style={{ position: 'relative' }} data-tab-dropdown>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenTabDropdown(!openTabDropdown);
                }}
                className="w-full border border-tebox-border focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 flex items-center justify-between"
                style={{
                  fontFamily: designSystem.typography.body.fontFamily,
                  fontSize: tebox.typography.body.fontSize,
                  lineHeight: tebox.typography.body.lineHeight,
                  minHeight: 48,
                  padding: `${tebox.spacing.stackGap} ${tebox.spacing.contentGutter}`,
                  borderRadius: tebox.radius.md,
                  backgroundColor: tebox.colors.bgPrimary,
                  color: activeBrandColors.textColor,
                  borderColor: tebox.colors.border,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: tebox.spacing.stackGap }}>
                  {(() => {
                    const t = golferDashboardTabs.find((tab) => tab.id === activeTab);
                    const lab = t?.label || 'Reservations';
                    return (
                      <>
                        <GolferDashboardNavSectionIcon
                          label={lab}
                          color={designSystem.colors.primary as string}
                        />
                        <span>{lab}</span>
                      </>
                    );
                  })()}
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    transform: openTabDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke={tebox.colors.textSecondary}
                    strokeWidth={ICON_STROKE.standard}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {openTabDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: tebox.colors.bgPrimary,
                    border: `1px solid ${tebox.colors.border}`,
                    borderRadius: tebox.radius.md,
                    boxShadow: 'none',
                    zIndex: 1000,
                    marginTop: tebox.spacing.microGap,
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  {golferDashboardTabs.map((tab) => {
                    const isSelected = activeTab === tab.id;
                    return (
                      <div
                        key={tab.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab(tab.id);
                          setOpenTabDropdown(false);
                        }}
                        style={{
                          padding: `${tebox.spacing.stackGap} ${tebox.spacing.contentGutter}`,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: tebox.spacing.stackGap,
                          backgroundColor: isSelected ? tebox.colors.bgSecondary : tebox.colors.bgPrimary,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = tebox.colors.bgSecondary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isSelected
                            ? tebox.colors.bgSecondary
                            : tebox.colors.bgPrimary;
                        }}
                      >
                        <GolferDashboardNavSectionIcon
                          label={tab.label}
                          color={
                            isSelected
                              ? (designSystem.colors.primary as string)
                              : tebox.colors.textSecondary
                          }
                        />
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontFamily: designSystem.typography.body.fontFamily,
                              fontSize: teboxDesignSystem.typography.body.fontSize,
                              fontWeight: isSelected ? '500' : '400',
                              color: activeBrandColors.textColor,
                            }}
                          >
                            {tab.label}
                          </div>
                        </div>
                        {isSelected && (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M13.3333 4L6 11.3333L2.66667 8"
                              stroke={designSystem.colors.primary || '#000000'}
                              strokeWidth={ICON_STROKE.standard}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Below header: sidebar + content row (compact: content only). */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flex: '1 1 auto',
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          alignItems: 'stretch',
          boxSizing: 'border-box',
        }}
      >
        {!isCompact ? (
          <aside
            aria-label="Dashboard sections"
            style={{
              flex: `0 0 ${DASHBOARD_INLINE_SIDEBAR_WIDTH_PX}px`,
              width: DASHBOARD_INLINE_SIDEBAR_WIDTH_PX,
              boxSizing: 'border-box',
              borderRight: `1px solid ${tebox.colors.border}`,
              backgroundColor: tebox.colors.bgSecondary,
              paddingTop: tebox.spacing.sectionGap,
              paddingBottom: tebox.spacing.sectionGap,
              paddingLeft: 0,
              paddingRight: 0,
              alignSelf: 'stretch',
            }}
          >
            <nav
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tebox.spacing.microGap,
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                paddingLeft: dashboardInsetLeft,
                paddingRight: dashboardInsetLeft,
              }}
            >
              {golferDashboardTabs.map((tab) => {
                const active = activeTab === tab.id;
                const iconColor = active
                  ? (designSystem.colors.primary as string)
                  : tebox.colors.textSecondary;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    aria-current={active ? 'page' : undefined}
                    className="focus-visible:outline focus-visible:outline-2"
                    style={{
                      width: '100%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: tebox.spacing.stackGapTight,
                      minHeight: 44,
                      paddingTop: tebox.spacing.stackGapTight,
                      paddingBottom: tebox.spacing.stackGapTight,
                      paddingLeft: tebox.spacing.stackGap,
                      paddingRight: tebox.spacing.stackGap,
                      margin: 0,
                      boxSizing: 'border-box',
                      backgroundColor: active ? tebox.colors.bgPrimary : 'transparent',
                      border: 'none',
                      borderRadius: tebox.radius.md,
                      cursor: 'pointer',
                      outlineColor: tebox.colors.focusRing,
                      transition: 'background-color 0.15s ease',
                      fontFamily: designSystem.typography.body.fontFamily,
                      fontSize: tebox.typography.body.fontSize,
                      fontWeight: active ? 500 : 500,
                      color: active ? activeBrandColors.textColor : tebox.colors.textSecondary,
                      lineHeight: tebox.typography.body.lineHeight,
                      textDecoration: 'none',
                      textAlign: 'left',
                    }}
                  >
                    <GolferDashboardNavSectionIcon label={tab.label} color={iconColor} />
                    <span style={{ minWidth: 0, flex: '1 1 auto', wordBreak: 'break-word', textAlign: 'left' }}>
                      {tab.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </aside>
        ) : null}

        <div style={{ flex: '1 1 auto', minWidth: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      {/* Main content — horizontal inset tighter on phones; safe-area aware */}
      <div
        style={{
          width: '100%',
          paddingTop: tebox.spacing.sectionGap,
          paddingBottom: `max(${tebox.spacing.pageInset}, env(safe-area-inset-bottom, 0px))`,
          paddingLeft: dashboardInsetLeft,
          paddingRight: dashboardInsetRight,
          boxSizing: 'border-box',
        }}
      >
        {/* Reservations Tab */}
        {activeTab === 0 && (
          <div>
            {/* Upcoming Reservations */}
            <div style={{ marginBottom: tebox.spacing.sectionGap }}>
              <h2 style={{
                ...designSystem.typography.h2,
                margin: 0,
                marginBottom: tebox.spacing.stackGap,
              }}>
                Upcoming Reservations
              </h2>
              
              {upcomingReservations.length === 0 ? (
                <GolferDashboardEmptyStateShell>
                  <p
                    style={{
                      ...designSystem.typography.body,
                      margin: 0,
                      color: tebox.colors.textSecondary,
                    }}
                  >
                    No upcoming reservations yet.
                  </p>
                </GolferDashboardEmptyStateShell>
              ) : (
                <BookingGolferReservationsTable
                  reservations={upcomingReservations}
                  variant="upcoming"
                  onViewDetails={handleViewDetails}
                  isCompactLayout={isCompact}
                  cardBrand={{
                    fontFamily: designSystem.typography.body.fontFamily as string,
                    textColor: activeBrandColors.textColor,
                    secondaryColor: activeBrandColors.secondaryColor,
                    accentColor:
                      activeBrandColors.accentColor ?? activeBrandColors.buttonColor ?? '',
                    primaryColor: activeBrandColors.primaryColor,
                  }}
                />
              )}
            </div>

            {/* Past Reservations */}
            <div
              style={{
                paddingTop: tebox.spacing.sectionGap,
              }}
            >
              <h2 style={{
                ...designSystem.typography.h2,
                margin: 0,
                marginBottom: tebox.spacing.stackGap,
              }}>
                Past Reservations
              </h2>
              {pastReservations.length === 0 ? (
                <GolferDashboardEmptyStateShell>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: designSystem.spacing.md,
                      maxWidth: 420,
                      marginInline: 'auto',
                    }}
                  >
                    <p style={{ ...designSystem.typography.body, margin: 0 }}>
                      No past reservations yet. After you play, completed rounds show up here.
                    </p>
                    <button
                      type="button"
                      onClick={onBackToBooking}
                      style={{
                        backgroundColor: designSystem.colors.primary,
                        color: activeBrandColors.primaryTextColor,
                        border: 'none',
                        borderRadius: designSystem.borderRadius.md,
                        padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                        fontSize: tebox.typography.body.fontSize,
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontFamily: designSystem.typography.body.fontFamily,
                        textTransform: 'none',
                      }}
                    >
                      Book a tee time
                    </button>
                  </div>
                </GolferDashboardEmptyStateShell>
              ) : (
                <BookingGolferReservationsTable
                  reservations={pastReservations.slice(0, 6)}
                  variant="past"
                  onViewDetails={handleViewDetails}
                  isCompactLayout={isCompact}
                  cardBrand={{
                    fontFamily: designSystem.typography.body.fontFamily as string,
                    textColor: activeBrandColors.textColor,
                    secondaryColor: activeBrandColors.secondaryColor,
                    accentColor:
                      activeBrandColors.accentColor ?? activeBrandColors.buttonColor ?? '',
                    primaryColor: activeBrandColors.primaryColor,
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Wallet Tab */}
        {activeTab === 1 && (
          <div>
            <h2 style={{
              ...designSystem.typography.h2,
              margin: 0,
              marginBottom: tebox.spacing.sectionGap,
            }}>Wallet</h2>
            <BookingGolferWalletBalanceTable
              ds={designSystem}
              walletBalance={walletBalance}
              walletMode={walletMode}
              walletAvailableCredit={walletAvailableCredit}
              walletCreditLimit={walletCreditLimit}
              walletLedgerStatus={walletLedgerStatus}
            />

            {walletLedgerId && walletMobilePassAccessToken ? (
              <div style={{ marginBottom: designSystem.spacing.lg }}>
                <WalletAddButtons
                  title="Add house account to mobile wallet"
                  mobilePassResource={{
                    kind: 'ledger',
                    id: walletLedgerId,
                    accessToken: walletMobilePassAccessToken,
                  }}
                />
              </div>
            ) : null}

            <div style={{ marginTop: designSystem.spacing.lg, marginBottom: tebox.spacing.sectionGap }}>
              <h2
                style={{
                  ...designSystem.typography.h2,
                  margin: 0,
                  marginBottom: tebox.spacing.stackGap,
                }}
              >
                Transaction History
              </h2>
              {walletTransactions.length > 0 ? (
                <BookingGolferWalletTransactionsGuestList
                  transactions={walletTransactions}
                  debitAccentColor={designSystem.colors.primary}
                />
              ) : (
                <GolferDashboardEmptyStateShell>
                  <p style={{ ...designSystem.typography.body, margin: 0, color: tebox.colors.textSecondary }}>
                    No transactions yet
                  </p>
                </GolferDashboardEmptyStateShell>
              )}
            </div>
          </div>
        )}

        {/* Gift cards tab */}
        {activeTab === 2 && customer && (
          <div>
            <h2 style={{
              ...designSystem.typography.h2,
              margin: 0,
              marginBottom: tebox.spacing.sectionGap,
            }}>Gift cards</h2>
            <BookingGolferGiftCardsPanel
              cards={customerGiftCards}
              loading={giftCardsLoading}
              customerId={customer.id}
              brand={{
                fontFamily: brandFontFamily,
                textColor: activeBrandColors.textColor,
                secondaryColor: activeBrandColors.secondaryColor,
                primaryColor: activeBrandColors.primaryColor,
                accentColor:
                  activeBrandColors.accentColor?.trim() ||
                  activeBrandColors.buttonColor ||
                  activeBrandColors.primaryColor,
                cardBorderRadius: designSystem.borderRadius.borderedShell,
              }}
            />
          </div>
        )}

        {/* Pass tab */}
        {activeTab === 3 && (
          <div>
            <h2 style={{
              ...designSystem.typography.h2,
              margin: 0,
              marginBottom: tebox.spacing.sectionGap,
            }}>Passes</h2>
            <BookingGolferPassesPanel
              ds={designSystem}
              passes={customerPasses}
              loading={passesLoading}
            />
          </div>
        )}

        {/* Membership Tab - Only for members */}
        {isMember(customer) && activeTab === 4 && (
          <div>
            <h2 style={{
              ...designSystem.typography.h2,
              margin: 0,
              marginBottom: tebox.spacing.sectionGap,
            }}>
              Membership
            </h2>
            {membershipData ? (
              <div>
                <BookingGolferMembershipOverviewCard ds={designSystem} membership={membershipData} />

                <div style={{ marginTop: designSystem.spacing.lg, marginBottom: tebox.spacing.sectionGap }}>
                  {paymentLinkExpiredMessage ? (
                    <GolferDashboardPaymentLinkExpiredBanner
                      message={paymentLinkExpiredMessage}
                      onDismiss={() => setPaymentLinkExpiredMessage(null)}
                    />
                  ) : null}
                  <h2
                    style={{
                      ...designSystem.typography.h2,
                      margin: 0,
                      marginBottom: tebox.spacing.stackGap,
                    }}
                  >
                    Payment schedule
                  </h2>
                  {membershipData.paymentSchedules && membershipData.paymentSchedules.length > 0 ? (
                    <BookingGolferPaymentScheduleTable
                      ds={designSystem}
                      payments={membershipData.paymentSchedules}
                      primaryButtonTextColor={activeBrandColors.primaryTextColor}
                      onExpiredPaymentLink={() =>
                        setPaymentLinkExpiredMessage(
                          'That payment link has expired. Please contact the club for a new link or other payment options.',
                        )
                      }
                    />
                  ) : (
                    <GolferDashboardEmptyStateShell>
                      <p style={{ ...designSystem.typography.body, margin: 0, color: tebox.colors.textSecondary }}>
                        No payment schedule available
                      </p>
                    </GolferDashboardEmptyStateShell>
                  )}
                </div>
              </div>
            ) : (
              <GolferDashboardEmptyStateShell>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: designSystem.spacing.md,
                    maxWidth: 420,
                    marginInline: 'auto',
                  }}
                >
                  <p style={{ ...designSystem.typography.body, margin: 0 }}>
                    No membership data found
                  </p>
                  <button
                    type="button"
                    onClick={onBackToBooking}
                    style={{
                      backgroundColor: designSystem.colors.primary,
                      color: activeBrandColors.primaryTextColor,
                      border: 'none',
                      borderRadius: designSystem.borderRadius.md,
                      padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontFamily: designSystem.typography.body.fontFamily,
                    }}
                  >
                    Book a Membership
                  </button>
                </div>
              </GolferDashboardEmptyStateShell>
            )}
          </div>
        )}

        {/* Account: profile + privacy & data */}
        {activeTab === (isMember(customer) ? 5 : 4) && (
          <div>
            <h2 style={{
              ...designSystem.typography.h2,
              margin: 0,
              marginBottom: tebox.spacing.stackGap,
            }}>
              Account
            </h2>
            
            <div style={{
              border: `1px solid ${tebox.colors.border}`,
              borderRadius: designSystem.borderRadius.md,
              backgroundColor: designSystem.colors.white,
              padding: designSystem.spacing.lg,
              width: '100%',
            }}>
              <div style={{ marginBottom: designSystem.spacing.lg }}>
                <h3 style={{
                  ...designSystem.typography.body,
                  fontWeight: '500',
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  marginBottom: designSystem.spacing.md
                }}>
                  Profile Information
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: designSystem.spacing.md,
                  marginBottom: designSystem.spacing.md
                }}>
                  <div>
                    <label style={{
                      ...designSystem.typography.caption,
                      display: 'block',
                      marginBottom: designSystem.spacing.xs,
                      fontWeight: '500'
                    }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      value={profileForm.firstName}
                      onChange={(e) => setProfileForm({...profileForm, firstName: e.target.value})}
                      style={{
                        width: '100%',
                        padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                        border: `1px solid ${tebox.colors.border}`,
                        borderRadius: designSystem.borderRadius.sm,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        fontFamily: designSystem.typography.body.fontFamily,
                        color: activeBrandColors.textColor,
                        backgroundColor: '#ffffff',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      ...designSystem.typography.caption,
                      display: 'block',
                      marginBottom: designSystem.spacing.xs,
                      fontWeight: '500'
                    }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={profileForm.lastName}
                      onChange={(e) => setProfileForm({...profileForm, lastName: e.target.value})}
                      style={{
                        width: '100%',
                        padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                        border: `1px solid ${tebox.colors.border}`,
                        borderRadius: designSystem.borderRadius.sm,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        fontFamily: designSystem.typography.body.fontFamily,
                        color: activeBrandColors.textColor,
                        backgroundColor: '#ffffff',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>
                
                <div style={{ marginBottom: designSystem.spacing.md }}>
                  <label style={{
                    ...designSystem.typography.caption,
                    display: 'block',
                    marginBottom: designSystem.spacing.xs,
                    fontWeight: '500'
                  }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                      border: `1px solid ${tebox.colors.border}`,
                      borderRadius: designSystem.borderRadius.sm,
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      fontFamily: designSystem.typography.body.fontFamily,
                      color: activeBrandColors.textColor,
                      backgroundColor: '#ffffff',
                      outline: 'none'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: designSystem.spacing.md }}>
                  <label style={{
                    ...designSystem.typography.caption,
                    display: 'block',
                    marginBottom: designSystem.spacing.xs,
                    fontWeight: '500'
                  }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                    style={{
                      width: '100%',
                      padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                      border: `1px solid ${tebox.colors.border}`,
                      borderRadius: designSystem.borderRadius.sm,
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      fontFamily: designSystem.typography.body.fontFamily,
                      color: activeBrandColors.textColor,
                      backgroundColor: '#ffffff',
                      outline: 'none'
                    }}
                  />
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: designSystem.spacing.md,
                  marginBottom: designSystem.spacing.md,
                }}>
                  <div>
                    <label style={{
                      ...designSystem.typography.caption,
                      display: 'block',
                      marginBottom: designSystem.spacing.xs,
                      fontWeight: '500'
                    }}>
                      Date of birth
                    </label>
                    <input
                      type="date"
                      value={profileForm.dateOfBirth}
                      onChange={(e) => setProfileForm({ ...profileForm, dateOfBirth: e.target.value })}
                      style={{
                        width: '100%',
                        padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                        border: `1px solid ${tebox.colors.border}`,
                        borderRadius: designSystem.borderRadius.sm,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        fontFamily: designSystem.typography.body.fontFamily,
                        color: activeBrandColors.textColor,
                        backgroundColor: '#ffffff',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      ...designSystem.typography.caption,
                      display: 'block',
                      marginBottom: designSystem.spacing.xs,
                      fontWeight: '500'
                    }}>
                      Customer type
                    </label>
                    <div
                      style={{
                        ...designSystem.typography.body,
                        padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                        border: `1px solid ${tebox.colors.border}`,
                        borderRadius: designSystem.borderRadius.sm,
                        backgroundColor: designSystem.colors.gray[50],
                        color: designSystem.colors.gray[600],
                      }}
                    >
                      {customer?.customerTypeName ||
                        (typeof customer?.customerType === 'string' ? customer.customerType : '—')}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: designSystem.spacing.lg }}>
                  <label style={{
                    ...designSystem.typography.caption,
                    display: 'block',
                    marginBottom: designSystem.spacing.xs,
                    fontWeight: '500'
                  }}>
                    Preferences
                  </label>
                  <textarea
                    value={profileForm.preferences}
                    onChange={(e) => setProfileForm({ ...profileForm, preferences: e.target.value })}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                      border: `1px solid ${tebox.colors.border}`,
                      borderRadius: designSystem.borderRadius.sm,
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      fontFamily: designSystem.typography.body.fontFamily,
                      color: activeBrandColors.textColor,
                      backgroundColor: '#ffffff',
                      outline: 'none',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>

              {profileMessage && (
                <div
                  style={{
                    ...designSystem.typography.caption,
                    marginBottom: designSystem.spacing.md,
                    color: profileMessage.type === 'ok' ? '#166534' : '#dc2626',
                  }}
                >
                  {profileMessage.text}
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: designSystem.spacing.md,
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  disabled={profileSaving}
                  onClick={handleCancelContact}
                  style={{
                    backgroundColor: 'transparent',
                    color: designSystem.colors.primary,
                    border: `1px solid ${designSystem.colors.primary}`,
                    borderRadius: designSystem.borderRadius.md,
                    padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    fontWeight: '500',
                    cursor: profileSaving ? 'not-allowed' : 'pointer',
                    fontFamily: designSystem.typography.body.fontFamily,
                    opacity: profileSaving ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={profileSaving}
                  onClick={handleSaveContact}
                  style={{
                    backgroundColor: designSystem.colors.primary,
                    color: activeBrandColors.primaryTextColor,
                    border: 'none',
                    borderRadius: designSystem.borderRadius.md,
                    padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    fontWeight: '500',
                    cursor: profileSaving ? 'not-allowed' : 'pointer',
                    fontFamily: designSystem.typography.body.fontFamily,
                    opacity: profileSaving ? 0.6 : 1,
                  }}
                >
                  {profileSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>

            <div style={{ marginTop: tebox.spacing.sectionGap }}>
            <h2 style={{
              ...designSystem.typography.h2,
              margin: 0,
              marginBottom: tebox.spacing.stackGap,
            }}>
              Privacy & data
            </h2>
            
            {/* Password Management */}
            <div style={{
              backgroundColor: designSystem.colors.white,
              border: `1px solid ${tebox.colors.border}`,
              borderRadius: designSystem.borderRadius.lg,
              padding: designSystem.spacing.xl,
              marginBottom: designSystem.spacing.lg
            }}>
              <h3 style={{
                ...designSystem.typography.body,
                fontWeight: '500',
                fontSize: teboxDesignSystem.typography.body.fontSize,
                marginBottom: designSystem.spacing.md
              }}>
                Password Management
              </h3>
              
              <div style={{ display: 'flex', gap: designSystem.spacing.md }}>
                <button
                  style={{
                    backgroundColor: 'transparent',
                    color: designSystem.colors.primary,
                    border: `1px solid ${designSystem.colors.primary}`,
                    borderRadius: designSystem.borderRadius.md,
                    padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: designSystem.typography.body.fontFamily
                  }}
                >
                  Change Password
                </button>
                <button
                  style={{
                    backgroundColor: 'transparent',
                    color: designSystem.colors.gray[600],
                    border: `1px solid ${tebox.colors.border}`,
                    borderRadius: designSystem.borderRadius.md,
                    padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: designSystem.typography.body.fontFamily
                  }}
                >
                  Reset Password
                </button>
              </div>
            </div>

            {/* Tee Sheet Privacy */}
            <div style={{
              backgroundColor: designSystem.colors.white,
              border: `1px solid ${tebox.colors.border}`,
              borderRadius: designSystem.borderRadius.lg,
              padding: designSystem.spacing.xl,
              marginBottom: designSystem.spacing.lg
            }}>
              <h3 style={{
                ...designSystem.typography.body,
                fontWeight: '500',
                fontSize: teboxDesignSystem.typography.body.fontSize,
                marginBottom: designSystem.spacing.md
              }}>
                Tee Sheet Privacy
              </h3>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: designSystem.spacing.md,
                border: `1px solid ${tebox.colors.border}`,
                borderRadius: designSystem.borderRadius.md
              }}>
                <div>
                  <div style={{
                    ...designSystem.typography.body,
                    fontWeight: '500',
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    marginBottom: designSystem.spacing.xs
                  }}>
                    Show My Name on Tee Sheet
                  </div>
                  <div style={{
                    ...designSystem.typography.caption,
                    color: designSystem.colors.gray[600]
                  }}>
                    Allow other members to see your name when you're playing
                  </div>
                </div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={customer?.showNameOnTeeSheet ?? true}
                      onChange={async (e) => {
                        const checked = e.target.checked;
                        const res = await customerAuthService.updateProfile({ showNameOnTeeSheet: checked });
                        if (res.ok) {
                          setCustomer((prev) =>
                            prev ? { ...prev, showNameOnTeeSheet: res.customer.showNameOnTeeSheet } : prev
                          );
                        } else {
                          console.error('Failed to update tee sheet privacy:', res.error);
                        }
                      }}
                      sx={golferDashboardMuiSwitchSx(designSystem.colors.primary)}
                    />
                  }
                  label=""
                  sx={{ margin: 0 }}
                />
              </div>
            </div>

            {/* Communication Preferences */}
            <div style={{
              backgroundColor: designSystem.colors.white,
              border: `1px solid ${tebox.colors.border}`,
              borderRadius: designSystem.borderRadius.lg,
              padding: designSystem.spacing.xl,
              marginBottom: designSystem.spacing.lg
            }}>
              <h3 style={{
                ...designSystem.typography.body,
                fontWeight: '500',
                fontSize: teboxDesignSystem.typography.body.fontSize,
                marginBottom: designSystem.spacing.md
              }}>
                Communication Preferences
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: designSystem.spacing.md }}>
                {[
                  { key: 'emailNotifications', label: 'Email Notifications', description: 'Receive important updates via email' },
                  { key: 'smsNotifications', label: 'SMS Notifications', description: 'Receive text message notifications' },
                  { key: 'reservationReminders', label: 'Reservation Reminders', description: 'Get reminded about upcoming reservations' },
                  { key: 'paymentReminders', label: 'Payment Reminders', description: 'Receive reminders about pending payments' },
                  { key: 'marketingEmails', label: 'Marketing Emails', description: 'Receive promotional and marketing communications' },
                  { key: 'promotionalOffers', label: 'Promotional Offers', description: 'Receive special offers and discounts' }
                ].map((pref) => (
                  <div key={pref.key} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: designSystem.spacing.md,
                    border: `1px solid ${tebox.colors.border}`,
                    borderRadius: designSystem.borderRadius.md
                  }}>
                    <div>
                      <div style={{
                        ...designSystem.typography.body,
                        fontWeight: '500',
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        marginBottom: designSystem.spacing.xs
                      }}>
                        {pref.label}
                      </div>
                      <div style={{
                        ...designSystem.typography.caption,
                        color: designSystem.colors.gray[600]
                      }}>
                        {pref.description}
                      </div>
                    </div>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={communicationPrefs[pref.key as keyof CommunicationPreferences]}
                          onChange={async (e) => {
                            const checked = e.target.checked;
                            const key = pref.key as keyof CommunicationPreferences;
                            const res = await customerAuthService.updateCommunicationPreferences({ [key]: checked });
                            if (res.ok) {
                              setCustomer((prev) =>
                                prev
                                  ? { ...prev, communicationPreferences: res.preferences }
                                  : prev
                              );
                            } else {
                              console.error('Failed to update communication preference:', res.error);
                            }
                          }}
                          sx={golferDashboardMuiSwitchSx(designSystem.colors.primary)}
                        />
                      }
                      label=""
                      sx={{ margin: 0 }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Data Management */}
            <div style={{
              backgroundColor: designSystem.colors.white,
              border: `1px solid ${tebox.colors.border}`,
              borderRadius: designSystem.borderRadius.lg,
              padding: designSystem.spacing.xl
            }}>
              <h3 style={{
                ...designSystem.typography.body,
                fontWeight: '500',
                fontSize: teboxDesignSystem.typography.body.fontSize,
                marginBottom: designSystem.spacing.md
              }}>
                Data Management
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: designSystem.spacing.md }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: designSystem.spacing.md,
                  border: `1px solid ${tebox.colors.border}`,
                  borderRadius: designSystem.borderRadius.md
                }}>
                  <div>
                    <div style={{
                      ...designSystem.typography.body,
                      fontWeight: '500',
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Export My Data
                    </div>
                    <div style={{
                      ...designSystem.typography.caption,
                      color: designSystem.colors.gray[600]
                    }}>
                      Download a copy of all your personal data
                    </div>
                  </div>
                  <button
                    style={{
                      backgroundColor: 'transparent',
                      color: designSystem.colors.primary,
                      border: `1px solid ${designSystem.colors.primary}`,
                      borderRadius: designSystem.borderRadius.md,
                      padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontFamily: designSystem.typography.body.fontFamily
                    }}
                  >
                    Export Data
                  </button>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: designSystem.spacing.md,
                  border: '1px solid #dc2626',
                  borderRadius: designSystem.borderRadius.md,
                  backgroundColor: '#fef2f2'
                }}>
                  <div>
                    <div style={{
                      ...designSystem.typography.body,
                      fontWeight: '500',
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      marginBottom: designSystem.spacing.xs,
                      color: '#dc2626'
                    }}>
                      Delete My Account
                    </div>
                    <div style={{
                      ...designSystem.typography.caption,
                      color: '#dc2626'
                    }}>
                      Permanently delete your account and all associated data
                    </div>
                  </div>
                  <button
                    style={{
                      backgroundColor: '#dc2626',
                      color: designSystem.colors.white,
                      border: 'none',
                      borderRadius: designSystem.borderRadius.md,
                      padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontFamily: designSystem.typography.body.fontFamily
                    }}
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
            </div>
          </div>
        )}
      </div>

      </div>

      </div>

      {/* Receipt Modal */}
      {receiptModalOpen && receiptDisplayReservation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: designSystem.spacing.lg
        }}>
          <div style={{
            backgroundColor: designSystem.colors.white,
            borderRadius: designSystem.borderRadius.lg,
            padding: designSystem.spacing.lg,
            maxWidth: '920px',
            width: '100%',
            maxHeight: 'min(85vh, 640px)',
            overflowY: 'auto',
            position: 'relative',
            boxSizing: 'border-box',
          }}>
            {/* Close Button */}
            <button
              onClick={closeReceiptModal}
              style={{
                position: 'absolute',
                top: designSystem.spacing.md,
                right: designSystem.spacing.md,
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: teboxDesignSystem.typography.h4.fontSize,
                cursor: 'pointer',
                color: designSystem.colors.gray[500],
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = tebox.colors.bgSecondary;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>

            {/* Receipt Header */}
            <div style={{
              textAlign: 'left',
              marginBottom: designSystem.spacing.md,
              paddingBottom: designSystem.spacing.md,
            }}>
              <h2 style={{
                ...designSystem.typography.h2,
                margin: 0,
                marginBottom: tebox.spacing.stackGap,
              }}>
                Reservation Receipt
              </h2>
            </div>

            {/* Receipt body: reservation summary (left) + ticket lines (right) */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: designSystem.spacing.md,
            }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: designSystem.spacing.md,
                  alignItems: 'stretch',
                }}
              >
                <div
                  style={{
                    flex: '1 1 340px',
                    minWidth: 0,
                    border: `1px solid ${tebox.colors.border}`,
                    borderRadius: designSystem.borderRadius.md,
                    padding: designSystem.spacing.md,
                  }}
                >
                  <div style={{
                    ...designSystem.typography.caption,
                    marginBottom: designSystem.spacing.sm,
                    fontWeight: 500,
                  }}>
                    Reservation details
                  </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: designSystem.spacing.sm,
                }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{
                      ...designSystem.typography.caption,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Confirmation
                    </div>
                    <div style={{
                      ...designSystem.typography.body,
                      fontWeight: '500',
                      fontVariantNumeric: 'tabular-nums',
                      wordBreak: 'break-all',
                    }}>
                      {receiptDisplayReservation.id}
                    </div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{
                      ...designSystem.typography.caption,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Course
                    </div>
                    <div style={{
                      ...designSystem.typography.body,
                      fontWeight: '500',
                      wordBreak: 'break-word',
                    }}>
                      {receiptDisplayReservation.course?.name || 'Unknown Course'}
                    </div>
                  </div>
                  <div>
                    <div style={{
                      ...designSystem.typography.caption,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Date
                    </div>
                    <div style={{
                      ...designSystem.typography.body,
                      whiteSpace: 'nowrap',
                    }}>
                      {formatBookingReservationTeeTime(receiptDisplayReservation).date}
                    </div>
                  </div>
                  <div>
                    <div style={{
                      ...designSystem.typography.caption,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Time
                    </div>
                    <div style={{
                      ...designSystem.typography.body,
                      fontVariantNumeric: 'tabular-nums'
                    }}>
                      {formatBookingReservationTeeTime(receiptDisplayReservation).time}
                    </div>
                  </div>
                  {(() => {
                    const slot = (receiptDisplayReservation as { teeTimeSlot?: { laneKey?: string; holeSet?: string } })
                      .teeTimeSlot;
                    const lane =
                      (slot?.laneKey && String(slot.laneKey).trim() && slot.laneKey !== 'default'
                        ? String(slot.laneKey).replace(/-/g, ' ')
                        : null) || parseLaneKeyFromBookingTeeTimeId(receiptDisplayReservation.teeTimeId);
                    const holes = formatBookingReceiptHoleSet(slot?.holeSet);
                    if (!lane && !holes) return null;
                    return (
                      <>
                        {lane ? (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{
                              ...designSystem.typography.caption,
                              marginBottom: designSystem.spacing.xs
                            }}>
                              Starting / lane
                            </div>
                            <div style={{ ...designSystem.typography.body }}>{lane}</div>
                          </div>
                        ) : null}
                        {holes ? (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{
                              ...designSystem.typography.caption,
                              marginBottom: designSystem.spacing.xs
                            }}>
                              Round
                            </div>
                            <div style={{ ...designSystem.typography.body }}>{holes}</div>
                          </div>
                        ) : null}
                      </>
                    );
                  })()}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{
                      ...designSystem.typography.caption,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Status
                    </div>
                    <span
                      {...bookingBrandInfoPillDataAttrs}
                      style={getBookingBrandInfoPillStyles(
                        golferReservationStatusAccent(
                          golferReservationStatusForDisplay(receiptDisplayReservation),
                          activeBrandColors.primaryColor,
                        ),
                        {
                          fontSize: teboxDesignSystem.typography.caption.fontSize,
                        },
                      )}
                    >
                      {golferReservationStatusLabel(
                        golferReservationStatusForDisplay(receiptDisplayReservation),
                      )}
                    </span>
                  </div>
                  {(() => {
                    const pay = receiptDisplayReservation as Reservation & {
                      paymentStatus?: string;
                      paymentMethod?: string | null;
                      paidAt?: string | null;
                    };
                    if (!pay.paymentStatus && !pay.paymentMethod && !pay.paidAt) return null;
                    return (
                      <>
                        {pay.paymentStatus ? (
                          <div>
                            <div style={{
                              ...designSystem.typography.caption,
                              marginBottom: designSystem.spacing.xs
                            }}>
                              Payment
                            </div>
                            <div style={{ ...designSystem.typography.body }}>
                              {String(pay.paymentStatus).replace(/_/g, ' ')}
                            </div>
                          </div>
                        ) : null}
                        {pay.paymentMethod ? (
                          <div>
                            <div style={{
                              ...designSystem.typography.caption,
                              marginBottom: designSystem.spacing.xs
                            }}>
                              Method
                            </div>
                            <div style={{ ...designSystem.typography.body }}>
                              {String(pay.paymentMethod).replace(/_/g, ' ')}
                            </div>
                          </div>
                        ) : null}
                        {pay.paidAt ? (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{
                              ...designSystem.typography.caption,
                              marginBottom: designSystem.spacing.xs
                            }}>
                              Paid
                            </div>
                            <div style={{
                              ...designSystem.typography.body,
                              fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                              lineHeight: 1.35,
                            }}>
                              {format(new Date(pay.paidAt), 'MMM d, yyyy · h:mm a')}
                            </div>
                          </div>
                        ) : null}
                      </>
                    );
                  })()}
                  <div>
                    <div style={{
                      ...designSystem.typography.caption,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Booked
                    </div>
                    <div style={{
                      ...designSystem.typography.body,
                      fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                      lineHeight: 1.35,
                    }}>
                      {format(new Date(receiptDisplayReservation.createdAt), 'MMM d, yyyy · h:mm a')}
                    </div>
                  </div>
                  <div>
                    <div style={{
                      ...designSystem.typography.caption,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Total
                    </div>
                    <div style={{
                      ...designSystem.typography.body,
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 500,
                      overflow: 'visible',
                      whiteSpace: 'nowrap',
                    }}>
                      {formatBookingReservationAmount(bookingReceiptTotalDollars(receiptDisplayReservation))}
                    </div>
                  </div>
                </div>

                {receiptDisplayReservation.playerCount > 0 &&
                  (!receiptDisplayReservation.players || receiptDisplayReservation.players.length === 0) && (
                  <div style={{
                    marginTop: designSystem.spacing.md,
                    paddingTop: designSystem.spacing.md,
                  }}>
                    <div style={{
                      ...designSystem.typography.caption,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Players
                    </div>
                    <div style={{ ...designSystem.typography.body }}>
                      {receiptDisplayReservation.playerCount} player
                      {receiptDisplayReservation.playerCount === 1 ? '' : 's'}
                    </div>
                  </div>
                )}

                {receiptDisplayReservation.players && receiptDisplayReservation.players.length > 0 && (
                  <div style={{
                    marginTop: designSystem.spacing.md,
                    paddingTop: designSystem.spacing.md,
                  }}>
                    <div style={{
                      ...designSystem.typography.caption,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      Players ({receiptDisplayReservation.playerCount})
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: designSystem.spacing.xs
                    }}>
                      {receiptDisplayReservation.players.map((player, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: `${designSystem.spacing.xs} ${designSystem.spacing.sm}`,
                          backgroundColor: tebox.colors.bgHighlight,
                          borderRadius: designSystem.borderRadius.sm
                        }}>
                          <div style={{
                            ...designSystem.typography.body,
                            fontWeight: '500',
                            fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                          }}>
                            {bookingPlayerRowLabel(player)}
                          </div>
                          <div style={{
                            ...designSystem.typography.caption,
                            fontSize: teboxDesignSystem.typography.caption.fontSize,
                          }}>
                            {bookingPlayerTypeLabel(player)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </div>

                <div
                  style={{
                    flex: '1 1 340px',
                    minWidth: 0,
                    border: `1px solid ${tebox.colors.border}`,
                    borderRadius: designSystem.borderRadius.md,
                    padding: designSystem.spacing.md,
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 'min(52vh, 420px)',
                  }}
                >
                  <div style={{
                    ...designSystem.typography.caption,
                    marginBottom: designSystem.spacing.sm,
                    fontWeight: 500,
                  }}>
                    Ticket details
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                    {(() => {
                      const lines = collectBookingReceiptLineItems(receiptDisplayReservation);
                      if (receiptDetailLoading && lines.length === 0) {
                        return (
                          <div style={{
                            ...designSystem.typography.caption,
                            color: tebox.colors.textSecondary,
                          }}>
                            Loading ticket details…
                          </div>
                        );
                      }
                      if (lines.length === 0) {
                        return (
                          <div style={{
                            ...designSystem.typography.caption,
                            color: tebox.colors.textSecondary,
                          }}>
                            No itemized line items on file for this reservation.
                          </div>
                        );
                      }
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: designSystem.spacing.xs }}>
                          {lines.map((line, idx) => (
                            <div
                              key={`${line.label}-${idx}`}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: designSystem.spacing.sm,
                                padding: `${designSystem.spacing.xs} 0`,
                                borderBottom:
                                  idx < lines.length - 1 ? `1px solid ${tebox.colors.border}` : undefined,
                              }}
                            >
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{
                                  ...designSystem.typography.body,
                                  fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                                  fontWeight: 500,
                                }}>
                                  {line.label}
                                </div>
                                {line.spotLabel ? (
                                  <div style={{
                                    ...designSystem.typography.caption,
                                    color: tebox.colors.textSecondary,
                                    marginTop: 2,
                                  }}>
                                    {line.spotLabel}
                                  </div>
                                ) : null}
                              </div>
                              <div style={{
                                ...designSystem.typography.body,
                                fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                                fontVariantNumeric: 'tabular-nums',
                                textAlign: 'right',
                                flexShrink: 0,
                              }}>
                                {line.qty} × {formatBookingReservationAmount(line.unitPrice)}
                                <br />
                                <span style={{ fontWeight: 500 }}>
                                  {formatBookingReservationAmount(line.lineTotal)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Receipt Footer */}
            <div style={{
              textAlign: 'left',
              marginTop: designSystem.spacing.md,
              paddingTop: designSystem.spacing.md,
            }}>
              <div style={{
                ...designSystem.typography.caption,
                marginBottom: designSystem.spacing.md
              }}>
                Thank you for your reservation!
              </div>
              
              {/* Action Buttons for Upcoming Reservations */}
              {receiptDisplayReservation && isUpcomingReservation(receiptDisplayReservation) && (
                <div style={{
                  display: 'flex',
                  gap: designSystem.spacing.md,
                  justifyContent: 'center',
                  marginBottom: designSystem.spacing.lg
                }}>
                  {canEditReservation(receiptDisplayReservation) && (
                    <button
                      onClick={handleEditReservation}
                      style={{
                        backgroundColor: 'transparent',
                        color: designSystem.colors.primary,
                        border: `1px solid ${designSystem.colors.primary}`,
                        borderRadius: designSystem.borderRadius.md,
                        padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: designSystem.typography.body.fontFamily
                      }}
                    >
                      Edit Reservation
                    </button>
                  )}
                  
                  {canCancelReservation(receiptDisplayReservation) && (
                    <button
                      onClick={handleCancelReservation}
                      style={{
                        backgroundColor: '#dc2626',
                        color: designSystem.colors.white,
                        border: 'none',
                        borderRadius: designSystem.borderRadius.md,
                        padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: designSystem.typography.body.fontFamily
                      }}
                    >
                      Cancel Reservation
                    </button>
                  )}
                </div>
              )}
              
              {/* Club Policies Notice */}
              {receiptDisplayReservation && isUpcomingReservation(receiptDisplayReservation) && clubPolicies && (
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: designSystem.borderRadius.md,
                  padding: designSystem.spacing.md,
                  marginBottom: designSystem.spacing.lg
                }}>
                  <div style={{
                    ...designSystem.typography.caption,
                    color: '#92400e',
                    textAlign: 'left'
                  }}>
                    <strong>Club Policies:</strong> {clubPolicies}
                  </div>
                </div>
              )}
              
              <button
                onClick={closeReceiptModal}
                style={{
                  backgroundColor: designSystem.colors.primary,
                  color: activeBrandColors.primaryTextColor,
                  border: 'none',
                  borderRadius: designSystem.borderRadius.md,
                  padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: designSystem.typography.body.fontFamily
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reservation Modal */}
      {showEditModal && selectedReservation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: designSystem.spacing.lg
        }}>
          <div style={{
            backgroundColor: designSystem.colors.white,
            borderRadius: designSystem.borderRadius.lg,
            padding: designSystem.spacing.xl,
            maxWidth: '500px',
            width: '100%',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={closeEditModal}
              style={{
                position: 'absolute',
                top: designSystem.spacing.md,
                right: designSystem.spacing.md,
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: teboxDesignSystem.typography.h4.fontSize,
                cursor: 'pointer',
                color: designSystem.colors.gray[500],
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%'
              }}
            >
              ×
            </button>

            <h2 style={{
              ...designSystem.typography.h2,
              marginBottom: designSystem.spacing.lg,
              textAlign: 'left'
            }}>
              Edit Reservation
            </h2>

            <p style={{
              ...designSystem.typography.body,
              marginBottom: designSystem.spacing.md,
              color: designSystem.colors.gray[600],
              fontSize: teboxDesignSystem.typography.body.fontSize,
            }}>
              Update special requests or notes for the pro shop. Tee time and player count cannot be changed here
              {clubContactPhone ? ` — call ${clubContactPhone} for other changes.` : '.'}
            </p>
            {selfServiceModalError && (
              <div style={{
                ...designSystem.typography.caption,
                color: '#dc2626',
                marginBottom: designSystem.spacing.md,
              }}>
                {selfServiceModalError}
              </div>
            )}
            {selectedReservation &&
            isBookerForReservation(selectedReservation) &&
            (selectedReservation.playerCount ?? 0) > 1 &&
            !['canceled', 'cancelled'].includes(String(selectedReservation.status || '').toLowerCase()) ? (
              <div
                style={{
                  marginBottom: designSystem.spacing.lg,
                  padding: designSystem.spacing.md,
                  borderRadius: designSystem.borderRadius.md,
                  border: `1px solid ${tebox.colors.border}`,
                  backgroundColor: tebox.colors.bgHighlight,
                  textAlign: 'left',
                }}
              >
                <p
                  style={{
                    ...designSystem.typography.body,
                    fontWeight: 500,
                    margin: `0 0 ${designSystem.spacing.xs} 0`,
                  }}
                >
                  Invite playing partners
                </p>
                <p
                  style={{
                    ...designSystem.typography.caption,
                    color: designSystem.colors.gray[600],
                    margin: `0 0 ${designSystem.spacing.sm} 0`,
                    lineHeight: 1.45,
                  }}
                >
                  Share a link so others can join open spots on this reservation. Generating a new link
                  replaces the old one.
                </p>
                {partnerInviteErrDash ? (
                  <p style={{ ...designSystem.typography.caption, color: '#dc2626', margin: `0 0 ${designSystem.spacing.xs} 0` }}>
                    {partnerInviteErrDash}
                  </p>
                ) : null}
                {partnerInviteUrlDash ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: designSystem.spacing.sm, alignItems: 'center', marginBottom: designSystem.spacing.sm }}>
                    <span
                      style={{
                        ...designSystem.typography.caption,
                        color: designSystem.colors.gray[800],
                        wordBreak: 'break-all',
                        flex: '1 1 200px',
                      }}
                    >
                      {partnerInviteUrlDash}
                    </span>
                    <button
                      type="button"
                      onClick={() => void navigator.clipboard.writeText(partnerInviteUrlDash)}
                      style={{
                        backgroundColor: designSystem.colors.primary,
                        color: activeBrandColors.primaryTextColor,
                        border: 'none',
                        borderRadius: designSystem.borderRadius.md,
                        padding: `${designSystem.spacing.xs} ${designSystem.spacing.md}`,
                        fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontFamily: designSystem.typography.body.fontFamily,
                      }}
                    >
                      Copy link
                    </button>
                  </div>
                ) : null}
                <button
                  type="button"
                  disabled={partnerInviteBusyDash}
                  onClick={() => void handleMintPartnerInviteDash()}
                  style={{
                    backgroundColor: designSystem.colors.gray[100],
                    color: designSystem.colors.gray[800],
                    border: `1px solid ${tebox.colors.border}`,
                    borderRadius: designSystem.borderRadius.md,
                    padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    fontWeight: 500,
                    cursor: partnerInviteBusyDash ? 'wait' : 'pointer',
                    fontFamily: designSystem.typography.body.fontFamily,
                  }}
                >
                  {partnerInviteBusyDash
                    ? 'Creating link…'
                    : partnerInviteUrlDash
                      ? 'Generate new link'
                      : 'Create invite link'}
                </button>
              </div>
            ) : null}
            <label style={{ ...designSystem.typography.caption, display: 'block', marginBottom: designSystem.spacing.xs }}>
              Notes
            </label>
            <textarea
              value={editNotesDraft}
              onChange={(e) => setEditNotesDraft(e.target.value)}
              rows={5}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: designSystem.spacing.sm,
                fontFamily: designSystem.typography.body.fontFamily,
                fontSize: teboxDesignSystem.typography.body.fontSize,
                border: `1px solid ${tebox.colors.border}`,
                borderRadius: designSystem.borderRadius.md,
                marginBottom: designSystem.spacing.lg,
              }}
            />
            <div style={{ display: 'flex', gap: designSystem.spacing.md, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={closeEditModal}
                style={{
                  backgroundColor: 'transparent',
                  color: designSystem.colors.primary,
                  border: `1px solid ${designSystem.colors.primary}`,
                  borderRadius: designSystem.borderRadius.md,
                  padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: designSystem.typography.body.fontFamily,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={selfServiceBusy}
                onClick={handleSaveReservationNotes}
                style={{
                  backgroundColor: designSystem.colors.primary,
                  color: activeBrandColors.primaryTextColor,
                  border: 'none',
                  borderRadius: designSystem.borderRadius.md,
                  padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  fontWeight: '500',
                  cursor: selfServiceBusy ? 'wait' : 'pointer',
                  fontFamily: designSystem.typography.body.fontFamily,
                  opacity: selfServiceBusy ? 0.7 : 1,
                }}
              >
                {selfServiceBusy ? 'Saving…' : 'Save notes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Reservation Modal */}
      {showCancelModal && selectedReservation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: designSystem.spacing.lg
        }}>
          <div style={{
            backgroundColor: designSystem.colors.white,
            borderRadius: designSystem.borderRadius.lg,
            padding: designSystem.spacing.xl,
            maxWidth: '500px',
            width: '100%',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={closeCancelModal}
              style={{
                position: 'absolute',
                top: designSystem.spacing.md,
                right: designSystem.spacing.md,
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: teboxDesignSystem.typography.h4.fontSize,
                cursor: 'pointer',
                color: designSystem.colors.gray[500],
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%'
              }}
            >
              ×
            </button>

            <h2 style={{
              ...designSystem.typography.h2,
              marginBottom: designSystem.spacing.lg,
              textAlign: 'left',
              color: '#dc2626'
            }}>
              Cancel Reservation
            </h2>

            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: designSystem.borderRadius.md,
              padding: designSystem.spacing.md,
              marginBottom: designSystem.spacing.lg
            }}>
              <div style={{
                ...designSystem.typography.caption,
                color: '#dc2626',
                textAlign: 'left'
              }}>
                Online cancellation is available when your tee time is more than {cancellationWindowHours}{' '}
                hour{cancellationWindowHours === 1 ? '' : 's'} away (club policy).
                {clubContactPhone
                  ? ` For help, call ${clubContactPhone}.`
                  : ' Contact the pro shop if you need help.'}
              </div>
            </div>
            {selfServiceModalError && (
              <div style={{
                ...designSystem.typography.caption,
                color: '#dc2626',
                marginBottom: designSystem.spacing.md,
                textAlign: 'left',
              }}>
                {selfServiceModalError}
              </div>
            )}

            <div style={{
              border: `1px solid ${tebox.colors.border}`,
              borderRadius: designSystem.borderRadius.md,
              padding: designSystem.spacing.lg,
              marginBottom: designSystem.spacing.lg
            }}>
              <h3 style={{
                ...designSystem.typography.body,
                fontWeight: '500',
                fontSize: teboxDesignSystem.typography.body.fontSize,
                marginBottom: designSystem.spacing.md
              }}>
                Reservation Details
              </h3>
              <div style={{
                ...designSystem.typography.caption,
                marginBottom: designSystem.spacing.xs
              }}>
                {selectedReservation.course?.name || 'Unknown Course'}
              </div>
              <div style={{
                ...designSystem.typography.caption,
                marginBottom: designSystem.spacing.xs
              }}>
                {formatBookingReservationTeeTime(selectedReservation).date}
              </div>
              <div style={{
                ...designSystem.typography.caption,
                marginBottom: designSystem.spacing.xs
              }}>
                {formatBookingReservationTeeTime(selectedReservation).time}
              </div>
              <div style={{
                ...designSystem.typography.caption
              }}>
                {selectedReservation.playerCount} players —{' '}
                {formatBookingReservationAmount(selectedReservation.totalAmount ?? 0)}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: designSystem.spacing.md,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}>
              <button
                type="button"
                onClick={closeCancelModal}
                style={{
                  backgroundColor: 'transparent',
                  color: designSystem.colors.primary,
                  border: `1px solid ${designSystem.colors.primary}`,
                  borderRadius: designSystem.borderRadius.md,
                  padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: designSystem.typography.body.fontFamily
                }}
              >
                Keep reservation
              </button>
              <button
                type="button"
                disabled={selfServiceBusy}
                onClick={handleConfirmCancelReservation}
                style={{
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: designSystem.borderRadius.md,
                  padding: `${designSystem.spacing.sm} ${designSystem.spacing.lg}`,
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  fontWeight: '500',
                  cursor: selfServiceBusy ? 'wait' : 'pointer',
                  fontFamily: designSystem.typography.body.fontFamily,
                  opacity: selfServiceBusy ? 0.7 : 1,
                }}
              >
                {selfServiceBusy ? 'Canceling…' : 'Yes, cancel reservation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GolferDashboard;